import { BACKEND_URL } from '../config/backendUrl';
import { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import '../styles/contractDoc.css';
import { withSignaturePlaceholder } from '../utils/contractSignature';
import { FILLED_FIELD_CLASSES } from '../utils/contractFields';
import { useLinkExpiry, linkExpiryOptions } from '../utils/linkExpiry';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import BullhornNotePromptDialog from '../components/BullhornNotePromptDialog';
import { useBullhornNoteHandler } from '../hooks/useBullhornNoteHandler';
import { useSenderFillableFields } from '../hooks/useSenderFillableFields';
import SignatureCaptureDialog from '../components/send/SignatureCaptureDialog';
import { toast } from 'sonner';
import { templateTypeLabel, templateTypeTagClass } from '../utils/templateType';
import { 
  FileText, 
  Send, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Eye,
  RefreshCw,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  User,
  Building2,
  Mail,
  FileCheck,
  Ban,
  Package,
  ClipboardEdit,
  Layers,
  Copy as CopyIcon,
  ExternalLink,
  BellRing,
  ShieldCheck,
  Shield,
  ShieldPlus,
  Search,
  Files,
  KeyRound,
  PenTool,
  Timer,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { formatPacketEstimate } from '../utils/duration';
import CompanyDocumentsTab from '../components/embed/CompanyDocumentsTab';
import { entityTypeToAdopt } from '../utils/embedEntityType';
import { applyDocumentFilters, NO_FILTERS } from '../utils/documentSearch';
import DocumentSearchBar from '../components/embed/DocumentSearchBar';
import PullQualificationsButton from '../components/credentials/PullQualificationsButton';

const API_URL = BACKEND_URL || '';

// Status configuration
const STATUS_CONFIG = {
  sent: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Sent' },
  opened: { icon: Eye, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Opened' },
  in_progress: { icon: FileText, color: 'text-amber-600', bg: 'bg-amber-100', label: 'In Progress' },
  completed: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Completed' },
  pending_approval: { icon: FileCheck, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Pending Approval' },
  approved: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Approved' },
  expired: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Expired' },
  cancelled: { icon: Ban, color: 'text-gray-600', bg: 'bg-gray-100', label: 'Cancelled' },
  // Credas-specific: HyreDocs couldn't generate/deliver the magic link
  // itself (Credas API error) — distinct from a document merely expiring.
  credas_failed: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Failed' },
};

// The actual pass/fail outcome of a Credas identity check — distinct from
// doc.status (which only says the candidate finished the process, not
// whether they passed). Only ever set once status is 'completed'; null
// until then. Never shown on the candidate portal — recruiter/compliance-
// only detail (see credas.repo.ts's toCredasListResp vs toCredasPortalResponse).
const VERIFICATION_RESULT_CONFIG = {
  pass: { color: 'text-emerald-700', bg: 'bg-emerald-100', label: 'Pass' },
  fail: { color: 'text-red-700', bg: 'bg-red-100', label: 'Fail' },
  refer: { color: 'text-amber-700', bg: 'bg-amber-100', label: 'Refer' },
  pending: { color: 'text-amber-700', bg: 'bg-amber-100', label: 'Pending Review' },
};

// Derives the outcome badge for a Credas row: the real pass/fail result once
// it exists, "Awaiting Results" while it doesn't (sent/in_progress, or
// completed but the lookup hasn't landed yet), "Cancelled" for a cancelled
// check (not "Fail" — a recruiter withdrawing a check isn't the same as the
// candidate failing it), and nothing extra for credas_failed (the main
// status badge already says "Failed" there — a link-generation error, not
// an identity-check outcome).
function credasOutcomeBadge(doc) {
  if (doc.status === 'cancelled') return { color: 'text-gray-600', bg: 'bg-gray-100', label: 'Cancelled' };
  if (doc.status === 'credas_failed') return null;
  return VERIFICATION_RESULT_CONFIG[doc.verification_result] || { color: 'text-slate-600', bg: 'bg-slate-100', label: 'Awaiting Results' };
}

// Entity type colors matching Bullhorn brand
const ENTITY_COLORS = {
  candidate: { bg: 'bg-[#4CAF50]', text: 'text-white', border: 'border-[#4CAF50]', label: 'Candidate' },
  contact: { bg: 'bg-[#F9A825]', text: 'text-white', border: 'border-[#F9A825]', label: 'Contact' },
  placement: { bg: 'bg-[#1a4a5e]', text: 'text-white', border: 'border-[#1a4a5e]', label: 'Placement' },
};

// Where a template actually delivers, mirroring the worker's own routing
// (packets.ts targetsFor / SendPacketPage.js's routing preview): a
// candidate/contact-typed template routes by its own entity_type; a
// placement-typed template routes by its send_to_candidate/send_to_contact
// flags (and may go to both). Kept in one place so the "Send to" toggle,
// the routing preview, and the actual send loop can never disagree.
function templateRouting(t) {
  const entity = (t.entity_type || 'candidate').toLowerCase();
  // send_to_candidate/send_to_contact arrive from the API as 0/1, never the
  // literal boolean `false` — a `!== false` check is always true for those,
  // silently ignoring a template explicitly disabled for the candidate side.
  const sendToCandidate = entity === 'placement' ? !!t.send_to_candidate : entity !== 'contact';
  const sendToContact = entity === 'placement' ? !!t.send_to_contact : entity === 'contact';
  return { sendToCandidate, sendToContact };
}

// Does this template actually go out on the given side ('candidate' | 'contact')?
function templateAllowsSide(t, side) {
  const { sendToCandidate, sendToContact } = templateRouting(t);
  return side === 'contact' ? sendToContact : sendToCandidate;
}

// Configure axios for embed
const embedApi = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

// Inline Login Component for iframe
function InlineLogin({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password,
      });
      
      const token = response.data.access_token;
      // Store in sessionStorage for this iframe session
      sessionStorage.setItem('embed_token', token);
      onLoginSuccess(token);
    } catch (err) {
      // Handle different error formats
      const errorData = err.response?.data;
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <FileText className="w-6 h-6 text-orange-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800">HyreDocs</h2>
          <p className="text-sm text-slate-500 mt-1">Sign in to view documents</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
              {error}
            </div>
          )}
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-orange-500 hover:bg-orange-600"
            disabled={loading}
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <User className="w-4 h-4 mr-2" />
            )}
            Sign In
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function BullhornEmbedPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [packets, setPackets] = useState([]);
  const [entityInfo, setEntityInfo] = useState(null);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedPackets, setSelectedPackets] = useState([]);
  // Changing the selection invalidates any preview rendered for the old
  // selection — clear it rather than leave a stale PDF/values panel showing
  // for a template that's no longer (or not yet) the one being sent.
  const toggleTemplate = (id) => { setSelectedTemplates(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]); setDocumentPreview(null); setDocumentPreviewValues({}); setDocumentsPreviewResults(null); };
  // Only one packet can be sent per send action — checking a second packet
  // while one is already selected is blocked rather than added to the
  // selection, so a send can never silently fan out across packets.
  const togglePacket = (id) => {
    if (!selectedPackets.includes(id) && selectedPackets.length > 0) {
      toast.error('Only one packet can be sent at a time — uncheck the current packet first.');
      return;
    }
    setSelectedPackets(prev => prev.includes(id) ? prev.filter(x => x !== id) : [id]);
    setPacketPreviewResults(null);
  };

  // Live search filters for the in-dialog template / packet / form
  // pickers. The Bullhorn embed iframe is the highest-volume surface
  // for our power users — a searchable list with keyboard scan beats
  // a 100-item native <select>.
  const [templateSearch, setTemplateSearch] = useState('');
  const [packetSearch, setPacketSearch] = useState('');
  const [formSearch, setFormSearch] = useState('');
  const [sendMode, setSendMode] = useState('document'); // 'document' | 'packet' | 'form'
  // What the Send dialog opens on (Settings -> Integrations -> Bullhorn ATS).
  const [defaultSendMode, setDefaultSendMode] = useState('document');
  const [sending, setSending] = useState(false);

  // Templates currently relevant to the send action — the selected
  // template(s) in document mode, or every template belonging to the
  // selected packet(s) in packet mode. Must come after sendMode/
  // selectedPackets/packets/selectedTemplates are all declared above —
  // referencing a const before its declaration throws.
  const activeSenderFillableTemplateIds = sendMode === 'packet'
    ? [...new Set(selectedPackets.flatMap((packetId) => packets.find((p) => p.id === packetId)?.template_ids || []))]
    : selectedTemplates;

  // Which recipient side(s) the currently-active template(s) actually
  // support — mirrors the same send_to_candidate/send_to_contact routing
  // the backend enforces (documents.ts's guard, packets.ts's targetsFor).
  // Before anything is selected, don't pre-disable anything — there's
  // nothing to route yet.
  const activeTemplatesForRouting = activeSenderFillableTemplateIds
    .map((id) => templates.find((t) => t.id === id))
    .filter(Boolean);

  // Requester/Admin (sender_fillable) field capture, including the
  // signature pad — shared with the portal's packet-send page via
  // useSenderFillableFields, so this is the single implementation both
  // surfaces use (see the hook for the full behaviour contract).
  const {
    senderFieldValues, setSenderFieldValues, senderFieldKey,
    signingKey, setSigningKey,
    signatureMode, setSignatureMode,
    typedSignature, setTypedSignature,
    signaturePadRef, initSignatureCanvas, saveSignature,
    senderFillableFields, unfilledSenderFields, buildPrefilledValues,
  } = useSenderFillableFields(activeTemplatesForRouting);

  const canSendToCandidateSide = activeTemplatesForRouting.length === 0
    || activeTemplatesForRouting.some((t) => templateAllowsSide(t, 'candidate'));
  const canSendToContactSide = activeTemplatesForRouting.length === 0
    || activeTemplatesForRouting.some((t) => templateAllowsSide(t, 'contact'));
  // "Both" is available whenever the checked set collectively needs both
  // sides — a candidate-only doc checked alongside a contact-only one
  // ("Client Assignment Details") is exactly this case: "Both" means each
  // document goes to whichever side it individually supports (already
  // correctly enforced server-side), not that every document goes to
  // both people.
  const canSendToBothSides = canSendToCandidateSide && canSendToContactSide;
  // The auto-correcting effect for these lives further down, after
  // entityType/placementRecipientType are declared (search
  // "Keep the \"Send to\" choice valid").
  // Dry-run preview state (packet-level "Test preview" and the single-document
  // preview, both POST .../test-preview and never create a document or send
  // anything — see handleTestPreviewPacket / handleTestPreviewDocument).
  const [previewingPacket, setPreviewingPacket] = useState(false);
  const [packetPreviewResults, setPacketPreviewResults] = useState(null);
  const [previewingDocument, setPreviewingDocument] = useState(false);
  const [documentPreview, setDocumentPreview] = useState(null); // { preview_url, fields, prefilled_count, total_field_count }
  const [documentPreviewValues, setDocumentPreviewValues] = useState({}); // field_id -> edited value
  // Multi-document preview — used when 2+ templates are selected in Document
  // mode, since the single-document editable-values panel above only makes
  // sense for exactly one. Mirrors packetPreviewResults: a dry-run PDF per
  // template, no editable panel, just prefill stats and an Open PDF link.
  const [previewingDocuments, setPreviewingDocuments] = useState(false);
  const [documentsPreviewResults, setDocumentsPreviewResults] = useState(null);
  const [viewingPdf, setViewingPdf] = useState(null); // { url: blob:..., label } — see openPreviewPdf
  // Forms state for the Form tab
  const [forms, setForms] = useState([]);
  const [formsEnabled, setFormsEnabled] = useState(false);
  const [selectedForm, setSelectedForm] = useState('');
  // Post-send "add a Bullhorn note?" prompt state
  // Single source of truth for the post-send Bullhorn note behaviour.
  // Mode (popup / toast / silent / off) + the default body/action come
  // from tenant settings. See useBullhornNoteHandler for the dispatch
  // logic + graceful fallback to popup on auto-log failure.
  const bullhornNote = useBullhornNoteHandler(embedApi);
  const [authToken, setAuthToken] = useState(null);
  const [authInitializing, setAuthInitializing] = useState(true);
  const [expandedDoc, setExpandedDoc] = useState(null);
  // Polish-pass state — recruiter-facing list filtering and bulk reminder.
  // `statusFilter` shrinks the visible list (mirrors the chips above it).
  // `bulkReminding` disables the header button while a bulk reminder is in
  // flight so a recruiter can't double-fire emails on a slow connection.
  const [statusFilter, setStatusFilter] = useState('all');
  // Free-text search and sent-date range, applied on top of the status chips.
  const [docSearch, setDocSearch] = useState(NO_FILTERS);
  const [bulkReminding, setBulkReminding] = useState(false);
  const [customMessage, setCustomMessage] = useState('');
  // How long the link stays valid: starts from the workspace default (Settings -> Users & Security).
  const { expiryHours, setExpiryHours } = useLinkExpiry(() => embedApi.get('/api/settings/link-expiry'), showSendDialog);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [editingEmail, setEditingEmail] = useState(false);
  // For placement entities: which side of the placement the recruiter
  // wants to send to. 'candidate' (worker), 'contact' (client contact),
  // or 'both' (one document to each). Defaults to candidate to match the
  // existing behavior. The Bullhorn entity lookup now returns both sides
  // so the toggle can pre-fill names + emails without an extra fetch.
  const [placementRecipientType, setPlacementRecipientType] = useState('candidate');

  // ── Credas state ──────────────────────────────────────────────────
  // Only shown when the tenant has Credas configured + enabled in
  // Settings → Integrations. Polled once on mount; we hide the button
  // entirely when the feature flag is off so recruiters who don't use
  // Credas see no extra UI.
  const [credasEnabled, setCredasEnabled] = useState(false);
  const [credasSending, setCredasSending] = useState(false);
  const [showCredasDialog, setShowCredasDialog] = useState(false);
  const [credasJourneys, setCredasJourneys] = useState([]);
  const [credasJourneysLoading, setCredasJourneysLoading] = useState(false);
  const [selectedCredasJourneyId, setSelectedCredasJourneyId] = useState('');
  // Actors are scoped per-journey (Credas), so this list is re-fetched
  // whenever the journey selection changes — see loadCredasActorsFor below.
  const [credasActors, setCredasActors] = useState([]);
  const [credasActorsLoading, setCredasActorsLoading] = useState(false);
  const [selectedCredasActorId, setSelectedCredasActorId] = useState('');
  const [credasHasActiveCheck, setCredasHasActiveCheck] = useState(false);
  // "Send via Email" / "Send via SMS" — see openCredasDialog for how these
  // are initialised and handleSendCredas for what each one triggers.
  const [credasViaEmail, setCredasViaEmail] = useState(false);
  const [credasViaSms, setCredasViaSms] = useState(false);
  // The candidate's email / phone as read from the Bullhorn fields mapped in
  // Settings → Credas (default: email / mobile) — see credas.ts's
  // /candidate-contact. Whichever is blank can't be ticked.
  const [credasContact, setCredasContact] = useState({ loading: false, email: '', phone: '' });
  const credasEmail = credasContact.email;
  const credasMobile = credasContact.phone;

  // ── Credential Request state ───────────────────────────────────────
  // Separate dialog from the Send Document flow because the candidate
  // input is fundamentally different (no template, no fields — just an
  // upload checklist tied to Bullhorn certification categories).
  const [showCredentialDialog, setShowCredentialDialog] = useState(false);
  const [credentialCategories, setCredentialCategories] = useState([]);
  const [qualificationsLabel, setQualificationsLabel] = useState(null); // this client's name for the Qualifications field
  const [credentialCategoriesLoading, setCredentialCategoriesLoading] = useState(false);
  const [credentialSearch, setCredentialSearch] = useState('');
  const [selectedCredentialIds, setSelectedCredentialIds] = useState([]); // array of bullhorn category ids
  const [credentialCustomMessage, setCredentialCustomMessage] = useState('');
  const [credentialRequiredMode, setCredentialRequiredMode] = useState('all'); // 'all' | 'optional'
  const [credentialSending, setCredentialSending] = useState(false);

  // Get parameters from URL
  const entityId = searchParams.get('entityId') || searchParams.get('id');
  const rawEntityType = searchParams.get('entityType') || searchParams.get('type') || 'candidate';
  // Bullhorn's own name for a company is ClientCorporation; the tab uses 'company'.
  const urlEntityType = rawEntityType.toLowerCase() === 'clientcorporation' ? 'company' : rawEntityType;
  // A Contact or Company tab whose Bullhorn URL still says entityType=candidate
  // (or says nothing) is corrected by the server, which looks the id up in
  // Bullhorn and reports what it really is. We only ever adopt that for a tab
  // that claimed 'candidate', so every explicitly-typed tab behaves as before.
  const [detectedEntityType, setDetectedEntityType] = useState(null);
  const entityType = detectedEntityType || urlEntityType;
  const token = searchParams.get('token'); // Optional auth token passed from Bullhorn
  
  // SSO parameters
  const ssoEmail = searchParams.get('ssoEmail');
  const ssoTimestamp = searchParams.get('ssoTimestamp');
  const ssoSignature = searchParams.get('ssoSignature');
  const autoLogin = searchParams.get('autoLogin') === 'true';
  // Set by the backend when the SSO redirect could identify the tenant but
  // NOT the specific Bullhorn user (UserID lookup failed). We refuse to
  // silently impersonate the first admin in that case — surfacing this
  // flag here so the UI can render an actionable "not provisioned" state.
  const ssoError = searchParams.get('ssoError');
  const ssoErrorUserId = searchParams.get('ssoUserId');

  // Keep the "Send to" choice valid — and helpful — as the checked
  // template(s) change.
  const wasBothPossibleRef = useRef(false);
  useEffect(() => {
    if (entityType !== 'placement') {
      wasBothPossibleRef.current = canSendToBothSides;
      return;
    }
    // The checked set just started needing both sides (e.g. a candidate-
    // only doc got checked alongside a contact-only "Client Assignment
    // Details") — default straight to Both rather than silently leaving
    // out whichever side wasn't already selected. Only fires on this
    // transition (not every render where it's already true), so manually
    // picking Candidate/Contact for a single dual-capable template isn't
    // fought back to Both on the next render.
    if (canSendToBothSides && !wasBothPossibleRef.current && placementRecipientType !== 'both') {
      setPlacementRecipientType('both');
    } else if (placementRecipientType === 'candidate' && !canSendToCandidateSide) {
      setPlacementRecipientType(canSendToContactSide ? 'contact' : 'candidate');
    } else if (placementRecipientType === 'contact' && !canSendToContactSide) {
      setPlacementRecipientType(canSendToCandidateSide ? 'candidate' : 'contact');
    } else if (placementRecipientType === 'both' && !canSendToBothSides) {
      setPlacementRecipientType(canSendToContactSide ? 'contact' : 'candidate');
    }
    wasBothPossibleRef.current = canSendToBothSides;
  }, [entityType, canSendToCandidateSide, canSendToContactSide, canSendToBothSides, placementRecipientType]);

  // Handle SSO login
  const handleSSOLogin = useCallback(async () => {
    if (!ssoEmail || !ssoTimestamp || !ssoSignature) return false;
    
    try {
      const response = await axios.post(`${API_URL}/api/auth/sso/bullhorn`, {
        email: ssoEmail,
        timestamp: parseInt(ssoTimestamp),
        signature: ssoSignature,
      });
      
      const newToken = response.data.access_token;
      sessionStorage.setItem('embed_token', newToken);
      sessionStorage.setItem('sso_user', JSON.stringify(response.data.user));
      setAuthToken(newToken);
      embedApi.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return true;
    } catch (err) {
      console.error('SSO login failed:', err);
      // Don't show error - fall back to manual login
      return false;
    }
  }, [ssoEmail, ssoTimestamp, ssoSignature]);

  // Set up auth token - try cookie (autoLogin), then SSO, then sessionStorage, then URL param
  useEffect(() => {
    const initAuth = async () => {
      try {
        // First try cookie-based auth (from SSO redirect)
        if (autoLogin) {
          try {
            const meResponse = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
            if (meResponse.data?.id) {
              setAuthToken('cookie');
              embedApi.defaults.withCredentials = true;
              return;
            }
          } catch (e) {
            // Cookie auth probe failed — fall through to SSO / form login below.
          }
        }

        // Then try SSO if parameters are present
        if (ssoEmail && ssoTimestamp && ssoSignature) {
          const ssoSuccess = await handleSSOLogin();
          if (ssoSuccess) return;
        }

        // Then try the URL token, followed by the stored iframe session.
        const storedToken = sessionStorage.getItem('embed_token');
        if (token) {
          setAuthToken(token);
          sessionStorage.setItem('embed_token', token);
          embedApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else if (storedToken) {
          setAuthToken(storedToken);
          embedApi.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        }
      } finally {
        // Do not render the login form until every automatic authentication
        // mechanism has had a chance to complete.
        setAuthInitializing(false);
      }
    };
    
    initAuth();
  }, [token, ssoEmail, ssoTimestamp, ssoSignature, handleSSOLogin, autoLogin]);

  // Handle login success from inline form
  const handleLoginSuccess = (newToken) => {
    setAuthToken(newToken);
    embedApi.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  };

  // Pre-fill recipient email when entity info loads. For placements we
  // switch the email source based on the selected recipient side
  // (candidate vs contact). "Both" uses the candidate's email as the
  // primary display; the send action expands into two POSTs.
  useEffect(() => {
    if (!entityInfo) return;
    if (entityType === 'placement') {
      if (placementRecipientType === 'contact') {
        setRecipientEmail(entityInfo.contact?.email || '');
      } else {
        // candidate or both
        setRecipientEmail(entityInfo.candidate?.email || entityInfo.email || '');
      }
    } else if (entityInfo.email) {
      setRecipientEmail(entityInfo.email);
    }
  }, [entityInfo, entityType, placementRecipientType]);

  // Also pre-fill email when Send dialog opens
  useEffect(() => {
    if (!showSendDialog || !entityInfo) return;
    if (recipientEmail) return;
    if (entityType === 'placement' && placementRecipientType === 'contact') {
      setRecipientEmail(entityInfo.contact?.email || '');
    } else if (entityInfo.email) {
      setRecipientEmail(entityInfo.email);
    }
  }, [showSendDialog, entityInfo, entityType, placementRecipientType, recipientEmail]);

  // Load data
  const loadData = useCallback(async () => {
    if (!entityId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    let reloadingAsDetectedType = false;
    try {
      // Fetch documents for this entity
      const docsResponse = await embedApi.get(`/api/documents/by-bullhorn-id/${entityId}`, {
        params: { entity_type: entityType }
      });
      // The tab said 'candidate' but Bullhorn says this id is a contact or a
      // company: switch to that type and load again (changing entityType
      // re-creates loadData, which re-runs the load effect). Stay in the
      // loading state meanwhile so the wrong UI never flashes.
      const adopted = entityTypeToAdopt(entityType, docsResponse.data?.entity_type);
      if (adopted) {
        reloadingAsDetectedType = true;
        setDetectedEntityType(adopted);
        return;
      }
      // A company tab is a read-only list of its contacts' documents. Nothing
      // below applies: no sending (templates/packets/forms/Credas), and the
      // credential-request / Credas lookups key off a PERSON's Bullhorn id, so
      // calling them with a company id could match an unrelated candidate.
      if (entityType === 'company') {
        setDocuments(docsResponse.data.documents || []);
        setEntityInfo(docsResponse.data.entity ? { ...docsResponse.data.entity, truncated: !!docsResponse.data.truncated } : null);
        return;
      }
      // Fetch credential requests filed against this Bullhorn entity
      // and merge them into the same list as documents — same row UI,
      // marked with a `kind: 'credential_request'` discriminator and a
      // shield icon. Failures are non-fatal (route may be missing on
      // older deployments).
      let credentialRequests = [];
      try {
        const crResp = await embedApi.get(
          `/api/credential-requests/by-bullhorn-id/${entityId}`,
          { params: { entity_type: entityType } }
        );
        credentialRequests = (crResp.data?.requests || []).map(r => ({
          ...r,
          kind: 'credential_request',
          // Project credential-request fields onto the document row shape
          // so existing chips / filters / expanded UI work without forks.
          template_name: `Credentials: ${(r.items || []).map(i => i.bullhorn_category_name).slice(0, 3).join(', ')}${(r.items || []).length > 3 ? ` +${r.items.length - 3}` : ''}`,
          candidate_email: r.candidate_email,
          candidate_name: r.candidate_name,
          sent_at: r.sent_at || r.created_at,
          completed_at: r.completed_at,
          share_url: r.short_url,
        }));
      } catch (e) {
        // Quietly ignore if endpoint isn't available yet.
      }

      // Same pattern again for Credas ID-verification checks — merged into
      // the same list with `kind: 'credas'`. Always prefixed "Credas -" so
      // it reads unambiguously next to regular documents and credential
      // requests. Status values ('sent'/'in_progress'/'completed'/
      // 'credas_failed') already match STATUS_CONFIG's own keys, so no
      // translation is needed here (see credas.ts's /initiate and webhook
      // handler for where that mapping happens).
      let credasChecks = [];
      try {
        const credasResp = await embedApi.get(`/api/credas/by-bullhorn-id/${entityId}`);
        credasChecks = (credasResp.data?.requests || []).map(r => ({
          ...r,
          kind: 'credas',
          id: r.id,
          template_name: r.name,
          candidate_email: r.candidate_email,
          candidate_name: r.candidate_name,
          sent_at: r.sent_at,
          completed_at: r.completed_at,
          share_url: r.magic_link,
        }));
      } catch (e) {
        // Quietly ignore — Credas may not be enabled for this tenant, or
        // this candidate may have no Credas history at all.
      }

      const docs = docsResponse.data.documents || [];
      const merged = [...credentialRequests, ...credasChecks, ...docs].sort((a, b) => {
        const ta = new Date(a.sent_at || a.created_at || 0).getTime();
        const tb = new Date(b.sent_at || b.created_at || 0).getTime();
        return tb - ta;
      });
      setDocuments(merged);
      setEntityInfo(docsResponse.data.entity || null);
      
      // Auto-populate email from entity info
      if (docsResponse.data.entity?.email) {
        setRecipientEmail(docsResponse.data.entity.email);
      }

      // Fetch templates
      const templatesResponse = await embedApi.get('/api/templates');
      setTemplates(templatesResponse.data || []);

      // Fetch packets
      const packetsResponse = await embedApi.get('/api/packets');
      setPackets(packetsResponse.data || []);

      try {
        const sendDefault = await embedApi.get('/api/settings/bullhorn-send-default');
        setDefaultSendMode(sendDefault.data?.default_mode || 'document');
      } catch (e) {
        setDefaultSendMode('document');
      }

      // Fetch forms — feature-flagged, so 403 is the normal "off" path
      // and we just hide the Form tab. Filter to the entity type so the
      // recruiter only sees forms targeted at the record they're on.
      try {
        const formsResponse = await embedApi.get('/api/forms');
        const allForms = (formsResponse.data || []).filter(f => f.status !== 'archived');
        const relevant = allForms.filter(
          f => f.entity_type === entityType || !f.entity_type
        );
        setForms(relevant);
        setFormsEnabled(true);
      } catch (formsErr) {
        if (formsErr.response?.status === 403) {
          setForms([]);
          setFormsEnabled(false);
        } else {
          // Surface unexpected errors but never break the doc/packet tabs.
          console.warn('Form list fetch failed:', formsErr);
        }
      }
      // ── Credas integration check ─────────────────────────────────
      // Shown only when this tenant has the integration enabled — gates the
      // "Send to Credas" button. Existing Credas checks for this candidate
      // already appear in the merged document list above (kind: 'credas'),
      // so there's no separate state to fetch here anymore.
      try {
        const credasCfg = await embedApi.get('/api/credas/config');
        setCredasEnabled(!!credasCfg.data?.enabled && !!credasCfg.data?.api_key_set);
      } catch (e) {
        setCredasEnabled(false);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in to HyreDocs.');
      }
    } finally {
      if (!reloadingAsDetectedType) setLoading(false);
    }
  }, [entityId, entityType]);

  useEffect(() => {
    if (authToken) {
      loadData();
      // Proactively ensure the tenant's Bullhorn session is alive for every
      // custom-tab iframe load — the user shouldn't need to visit the main
      // app's Settings page to wake the API back up.
      embedApi.post('/api/tenants/me/bullhorn/ensure-connection').catch(e => console.warn('Bullhorn ensure-connection:', e?.message));
    } else {
      setLoading(false);
    }
  }, [authToken, loadData]);

  // ── Send the candidate to Credas (creates the no-contact process
  // and resolves the magic link, which we display + copy-to-clipboard
  // so the recruiter can paste it anywhere or trust the email merge
  // tag to embed it on subsequent packet sends).
  // Opens the journey-picker dialog instead of sending immediately — fetches
  // the tenant's configured journeys plus its saved default (Settings →
  // Credas) in parallel, and pre-selects that default so the common case
  // ("just use what's configured") is a single click.
  const openCredasDialog = async () => {
    setShowCredasDialog(true);
    // Nothing is ticked until the candidate's contact details and the
    // tenant's defaults (Settings → Credas) have both loaded — see below.
    setCredasViaEmail(false);
    setCredasViaSms(false);
    setCredasContact({ loading: true, email: '', phone: '' });
    // On a placement tab the person is the placement's candidate, not the
    // placement itself.
    const credasBullhornId = entityInfo?.candidate?.bullhorn_id || entityId;
    const contactPromise = embedApi.get(`/api/credas/candidate-contact/${credasBullhornId}`)
      .then((r) => r.data)
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not read the candidate's contact details from Bullhorn");
        return null;
      });
    let credasDefaults = {};
    // `documents` already holds this candidate's merged list (incl. kind:
    // 'credas' rows, fetched by-bullhorn-id in loadData) — no extra fetch
    // needed to check for an existing active check.
    setCredasHasActiveCheck(documents.some((d) => d.kind === 'credas' && ['sent', 'in_progress'].includes(d.status)));
    setCredasJourneysLoading(true);
    try {
      const [journeysResp, configResp] = await Promise.all([
        embedApi.get('/api/credas/journeys'),
        embedApi.get('/api/credas/config'),
      ]);
      const journeys = journeysResp.data?.journeys || [];
      setCredasJourneys(journeys);
      credasDefaults = configResp.data || {};
      const defaultId = configResp.data?.journey_id || '';
      const journeyId = journeys.some(j => j.id === defaultId) ? defaultId : (journeys[0]?.id || defaultId);
      setSelectedCredasJourneyId(journeyId);
      if (journeyId) loadCredasActorsFor(journeyId);
    } catch (e) {
      toast.error('Could not load Credas journeys');
      setCredasJourneys([]);
    } finally {
      setCredasJourneysLoading(false);
    }
    // Apply the tenant's "tick by default" settings, but never tick a channel
    // the candidate has no value for.
    const contact = await contactPromise;
    const email = (contact?.email || '').trim();
    const phone = (contact?.phone || '').trim();
    setCredasContact({ loading: false, email, phone });
    setCredasViaEmail(credasDefaults.send_email_default !== false && Boolean(email));
    setCredasViaSms(credasDefaults.send_sms_default === true && Boolean(phone));
  };

  // Actors are scoped per-journey — re-fetched every time the journey
  // selection changes, not just once when the dialog opens.
  const loadCredasActorsFor = async (journeyId) => {
    setCredasActorsLoading(true);
    try {
      const { data } = await embedApi.get(`/api/credas/journeys/${journeyId}/actors`);
      const actors = data?.actors || [];
      setCredasActors(actors);
      const defaultId = data?.default_actor_id;
      setSelectedCredasActorId(actors.some(a => a.id === defaultId) ? defaultId : (actors[0]?.id ?? defaultId ?? ''));
    } catch (e) {
      setCredasActors([]);
      setSelectedCredasActorId('');
    } finally {
      setCredasActorsLoading(false);
    }
  };

  const handleCredasJourneyChange = (journeyId) => {
    setSelectedCredasJourneyId(journeyId);
    if (journeyId) loadCredasActorsFor(journeyId);
  };

  const handleSendCredas = async () => {
    if (!entityInfo) return;
    const cand = entityInfo.candidate || entityInfo;
    const email = credasEmail;
    if (!credasViaEmail && !credasViaSms) {
      toast.error('Choose Send via Email, Send via SMS, or both.');
      return;
    }
    const fullName = (cand?.name || `${cand?.first_name || ''} ${cand?.last_name || ''}`).trim();
    const parts = fullName.split(/\s+/);
    const firstName = cand?.first_name || parts[0] || '';
    const lastName = cand?.last_name || (parts.length > 1 ? parts.slice(1).join(' ') : '(N/A)');

    setCredasSending(true);
    try {
      const { data } = await embedApi.post('/api/credas/initiate', {
        candidate_email: email,
        first_name: firstName,
        last_name: lastName,
        phone: credasMobile || undefined,
        send_email: credasViaEmail && Boolean(credasEmail),
        send_sms: credasViaSms && Boolean(credasMobile),
        candidate_bullhorn_id: cand?.bullhorn_id || entityId,
        journey_id: selectedCredasJourneyId || undefined,
        actor_id: selectedCredasActorId || undefined,
        // Already fetched for the journey dropdown — saves the backend a
        // redundant lookup to Credas for something we already have.
        journey_name: credasJourneys.find(j => j.id === selectedCredasJourneyId)?.name,
      });
      if (data?.status === 'credas_failed') {
        toast.error(`Credas failed: ${data.last_error || 'unknown error'}`);
      } else if (data?.magic_link) {
        toast.success('Credas link generated — see it in the list below.');
      } else {
        toast.success('Credas process created (link still resolving).');
      }
      setShowCredasDialog(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not contact Credas.');
    } finally {
      setCredasSending(false);
    }
  };

  // Shared by handleSendDocument and handleTestPreviewDocument — the preview
  // must resolve values for the exact same recipient a real send would use.
  // For placement entities, build the recipient list based on the chosen
  // side. "Both" expands into two sends — one to each; a preview only needs
  // one of them, since the rendered content is identical either way (the
  // Bullhorn record it resolves against doesn't depend on which side
  // receives the email — see prefillEntityId in documents.ts).
  const buildDocumentRecipientList = () => {
    let recipientList;
    if (entityType === 'placement') {
      const candEmail = entityInfo?.candidate?.email || entityInfo?.email || '';
      const candName = entityInfo?.candidate?.name || entityInfo?.name || 'Candidate';
      const contactEmail = entityInfo?.contact?.email || '';
      const contactName = entityInfo?.contact?.name || 'Contact';
      // entityId here is the PLACEMENT's own Bullhorn id — never the
      // recipient person's. Each side must carry its own person id (falling
      // back to entityId only if Bullhorn didn't give us one), same as the
      // already-correct handleSendPacket below, or the backend stores the
      // placement id as the candidate's Bullhorn id and getOrCreateCandidateAccount
      // later throws a false "linked to a different Bullhorn candidate" conflict
      // for any candidate who already has a portal account under their real id.
      const candBhId = entityInfo?.candidate?.bullhorn_id || entityId;
      const contactBhId = entityInfo?.contact?.bullhorn_id || entityId;
      if (placementRecipientType === 'both') {
        recipientList = [];
        if (candEmail) recipientList.push({ side: 'candidate', name: candName, email: candEmail, bullhornId: candBhId });
        if (contactEmail) recipientList.push({ side: 'contact', name: contactName, email: contactEmail, bullhornId: contactBhId });
        if (recipientList.length === 0) {
          toast.error('Neither candidate nor contact has an email in Bullhorn — add one first.');
          return;
        }
      } else if (placementRecipientType === 'contact') {
        if (!contactEmail) {
          toast.error('The linked client contact has no email in Bullhorn.');
          return;
        }
        recipientList = [{ side: 'contact', name: contactName, email: contactEmail, bullhornId: contactBhId }];
      } else {
        // candidate
        const useEmail = recipientEmail || candEmail;
        if (!useEmail) {
          toast.error('The candidate has no email in Bullhorn — enter one above.');
          return;
        }
        recipientList = [{ side: 'candidate', name: candName, email: useEmail, bullhornId: candBhId }];
      }
    } else {
      if (!recipientEmail) return;
      recipientList = [{ side: entityType, name: entityInfo?.name || 'Unknown', email: recipientEmail, bullhornId: entityId }];
    }
    return recipientList;
  };

  // Dry run: resolve Bullhorn/candidate-memory values for the currently
  // selected single template and render a flattened preview PDF, without
  // creating a document or sending anything. Seeds the editable pre-fill
  // panel from the resolved values so the recruiter can adjust before
  // sending — same idea as the packet-level preview above.
  const handleTestPreviewDocument = async () => {
    if (selectedTemplates.length !== 1 || !entityId) return;
    const templateId = selectedTemplates[0];
    const recipientList = buildDocumentRecipientList();
    if (!recipientList || !recipientList.length) return; // buildDocumentRecipientList already toasted why
    const r = recipientList[0];
    // Re-rendering after the recruiter edited values in the preview panel
    // must reflect those edits, not re-resolve fresh from Bullhorn — only
    // meaningful when re-previewing the SAME template that's already open.
    const editedValues = documentPreview?.templateId === templateId ? documentPreviewValues : {};
    const prefilledForTemplate = { ...buildPrefilledValues(templateId), ...editedValues };
    setPreviewingDocument(true);
    setDocumentPreview(null);
    try {
      const resp = await embedApi.post('/api/documents/test-preview', {
        template_id: templateId,
        recipient: {
          entity_type: entityType,
          bullhorn_id: r.bullhornId,
          name: r.name,
          email: r.email,
          ...(entityType === 'placement' ? { placement_recipient_type: r.side, placement_id: entityId } : {}),
        },
        ...(Object.keys(prefilledForTemplate).length ? { prefilled_values: prefilledForTemplate } : {}),
      });
      setDocumentPreview({ templateId, ...resp.data });
      setDocumentPreviewValues(
        Object.fromEntries((resp.data?.fields || []).map((f) => [f.field_id, f.value ?? ''])),
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview render failed');
    } finally {
      setPreviewingDocument(false);
    }
  };

  // Text templates (source_format "html") can't go out with a blank field, so
  // the preview opens by itself once the sender's own fields are filled, and
  // Send waits until every field has a value. `textTemplateBlank` is null while
  // the preview hasn't loaded yet, otherwise the fields still empty.
  const selectedTextTemplate = selectedTemplates.length === 1
    ? templates.find((t) => t.id === selectedTemplates[0] && t.source_format === 'html') || null
    : null;
  const textTemplateSelected = selectedTemplates.some(
    (id) => templates.find((t) => t.id === id)?.source_format === 'html',
  );
  const textTemplateBlank = !selectedTextTemplate || documentPreview?.templateId !== selectedTextTemplate.id
    ? null
    : (documentPreview.fields || []).filter(
        (f) => !f.auto_pending && String(documentPreviewValues[f.field_id] ?? '').trim() === '',
      );
  const textTemplateSenderValues = selectedTextTemplate ? JSON.stringify(buildPrefilledValues(selectedTextTemplate.id)) : '';
  useEffect(() => {
    if (!showSendDialog || sendMode !== 'document' || !selectedTextTemplate || !recipientEmail) return undefined;
    if (unfilledSenderFields.length > 0) return undefined;
    const timer = setTimeout(() => {
      if (!previewingDocument) handleTestPreviewDocument();
    }, 600);
    return () => clearTimeout(timer);
    // The preview is re-rendered when the template, recipient or the sender's own
    // values change — not on every render and not on the panel's own edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSendDialog, sendMode, selectedTextTemplate?.id, recipientEmail, unfilledSenderFields.length, textTemplateSenderValues]);

  // Dry run for 2+ selected templates at once — the single-document preview
  // above only makes sense for exactly one (it seeds an editable-values
  // panel keyed by bare field id, which can't disambiguate the same field
  // id appearing on more than one selected template). Renders one PDF per
  // template instead, same idea as handleTestPreviewPacket: no editable
  // panel, just prefill stats and an Open PDF link. Uses whichever "Send
  // to" side each template actually routes to — skips one that wouldn't be
  // sent under the current selection at all, matching the "Selected
  // documents" routing preview above and the real send.
  const handleTestPreviewDocuments = async () => {
    if (selectedTemplates.length < 2 || !entityId) return;
    const recipientList = buildDocumentRecipientList();
    if (!recipientList || !recipientList.length) return; // buildDocumentRecipientList already toasted why

    setPreviewingDocuments(true);
    setDocumentsPreviewResults(null);
    try {
      const results = await Promise.all(selectedTemplates.map(async (templateId) => {
        const t = templates.find((x) => x.id === templateId);
        const templateName = t?.name || 'Document';
        const r = entityType === 'placement'
          ? recipientList.find((cand) => templateAllowsSide(t, cand.side))
          : recipientList[0];
        if (!r) {
          return { templateId, templateName, skipped: true };
        }
        const prefilledForTemplate = buildPrefilledValues(templateId);
        try {
          const resp = await embedApi.post('/api/documents/test-preview', {
            template_id: templateId,
            recipient: {
              entity_type: entityType,
              bullhorn_id: r.bullhornId,
              name: r.name,
              email: r.email,
              ...(entityType === 'placement' ? { placement_recipient_type: r.side, placement_id: entityId } : {}),
            },
            ...(Object.keys(prefilledForTemplate).length ? { prefilled_values: prefilledForTemplate } : {}),
          });
          return { templateId, templateName, route: r.side, ...resp.data };
        } catch (error) {
          return { templateId, templateName, error: true };
        }
      }));
      setDocumentsPreviewResults(results);
      toast.success(`Rendered ${results.filter((r) => r.preview_url).length} preview PDF(s)`);
    } finally {
      setPreviewingDocuments(false);
    }
  };

  const handleSendDocument = async () => {
    if (selectedTemplates.length === 0 || !entityId) return;
    if (unfilledSenderFields.length > 0) {
      toast.error(`Please fill in before sending: ${unfilledSenderFields.map((f) => f.field.label).join(', ')}`);
      return;
    }
    if (textTemplateSelected) {
      if (selectedTemplates.length > 1) {
        toast.error('A text template has to be sent on its own.');
        return;
      }
      if (textTemplateBlank === null) {
        toast.error('Still preparing the document — try again in a moment.');
        return;
      }
      if (textTemplateBlank.length > 0) {
        toast.error(`Please fill in before sending: ${textTemplateBlank.map((f) => f.label).join(', ')}`);
        return;
      }
    }
    const recipientList = buildDocumentRecipientList();
    if (!recipientList || !recipientList.length) return; // buildDocumentRecipientList already toasted why

    setSending(true);
    try {
      let lastResp = null;
      let ok = 0;
      const failed = [];
      const skipped = [];
      for (const templateId of selectedTemplates) {
        // Only this template's own sender-filled values, stripped back to
        // plain field_id. Values reviewed/edited in the "Preview" panel win
        // over what was there before the preview was opened, since they're
        // the most recent explicit thing the recruiter did — but only for
        // the template that was actually previewed.
        const prefilledForTemplate = {
          ...buildPrefilledValues(templateId),
          ...(documentPreview?.templateId === templateId ? documentPreviewValues : {}),
        };
        for (const r of recipientList) {
          // A placement send picks one or more sides (candidate/contact),
          // but a checked template may only be routed to ONE of them (e.g.
          // a contact-only "Client Assignment Details" template must never
          // go to the candidate just because "Candidate" — or "Both" — was
          // picked). Mirrors the packet flow's server-side filtering
          // (packets.ts targetsFor) so a single-template send can't leak a
          // document to the wrong party.
          if (entityType === 'placement') {
            const fullTemplate = templates.find((t) => t.id === templateId);
            if (fullTemplate && !templateAllowsSide(fullTemplate, r.side)) {
              skipped.push({ side: r.side, templateId, name: fullTemplate.name });
              continue;
            }
          }
          try {
            const resp = await embedApi.post('/api/documents/send', {
              template_id: templateId,
              recipient: {
                entity_type: entityType,
                bullhorn_id: r.bullhornId,
                name: r.name,
                email: r.email,
                ...(entityType === 'placement' ? { placement_recipient_type: r.side, placement_id: entityId } : {}),
              },
              message: customMessage || undefined,
              expiry_hours: expiryHours,
              ...(Object.keys(prefilledForTemplate).length ? { prefilled_values: prefilledForTemplate } : {}),
            });
            lastResp = resp;
            ok += 1;
          } catch (err) {
            failed.push({ side: r.side, email: r.email, templateId, err });
          }
        }
      }

      const skipNote = skipped.length
        ? ` (${skipped.length} skipped — not enabled for ${[...new Set(skipped.map((s) => s.side))].join('/')})`
        : '';
      if (ok > 0 && failed.length === 0) {
        toast.success((ok === 1 ? 'Document sent successfully!' : `Sent ${ok} documents (candidate + contact)`) + skipNote);
      } else if (ok > 0 && failed.length > 0) {
        toast.warning(`Sent ${ok}, ${failed.length} failed (${failed.map(f => f.side).join(', ')})${skipNote}`);
      } else if (failed.length === 0 && skipped.length > 0) {
        // Every selected template was routed away by its own send-to
        // setting — nothing was actually a failure, so don't throw into the
        // generic error toast below; tell the recruiter plainly instead.
        toast.error(`Nothing sent — none of the selected documents are enabled for ${[...new Set(skipped.map((s) => s.side))].join('/')}.`);
        return;
      } else {
        throw failed[0] && failed[0].err ? failed[0].err : new Error('Send failed');
      }

      setShowSendDialog(false);
      setSelectedTemplates([]);
      setCustomMessage('');
      setEditingEmail(false);
      setDocumentPreview(null);
      setDocumentPreviewValues({});
      loadData(); // Refresh the list

      // Prompt the recruiter to log a note against the Bullhorn record.
      const tpl = templates.find((t) => t.id === selectedTemplates[0]);
      const recipientLabel = recipientList.map(r => `${r.name} <${r.email}>`).join(', ');
      bullhornNote.fire({
        entityType,
        entityId,
        entityName: entityInfo?.name || '',
        documentId: lastResp?.data?.document_id || null,
        context: {
          template_name: tpl?.name || 'document',
          document_type: tpl?.name || 'document',
          form_name: null,
          recipient_name: recipientList.map(r => r.name).join(', '),
          recipient_email: recipientList.map(r => r.email).join(', '),
          sender_name: '', // Embed has no logged-in user context — sender_name left blank.
        },
      });
    } catch (error) {
      // Handle different error formats
      const errorData = error.response?.data;
      let errorMessage = 'Failed to send document';
      
      if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        // Pydantic validation errors
        errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  // Shared by handleSendPacket and handleTestPreviewPacket so a preview can
  // never show something different from what a real send would do — same
  // reasoning as SendPacketPage.js's buildSendPayload. Returns the
  // placement/recipient portion of the /api/packets/{send,test-preview}
  // body (everything except packet_id).
  const buildPacketRoutingPayload = () => {
    // Requires the current Bullhorn record to expose both parties — only
    // true for placement entities. From candidate/contact-only views the
    // contact half can't be routed, so we fall back to the single-recipient
    // flow and the backend's existing entity-type mismatch handling.
    const candEmail = entityInfo?.candidate?.email;
    const contactEmail = entityInfo?.contact?.email;
    const hasPlacementContext =
      entityType === 'placement' && candEmail && contactEmail;

    // "Both" is the ONLY case that auto-routes each template to its own
    // side (candidate-docs to the candidate, contact-docs to the client) —
    // that's what "Both" means. Picking "Candidate" or "Contact" must
    // honor that explicit choice: only templates enabled for that one side
    // go out, and a template that's contact-only (or candidate-only) is
    // skipped rather than silently rerouted to whoever was picked. The
    // single-recipient branches below already do that filtering
    // server-side via each template's send_to_candidate/send_to_contact
    // flags (packets.ts targetsFor) — see the routing preview in the
    // dialog for exactly which documents that resolves to.
    const useRouting = hasPlacementContext && placementRecipientType === 'both';

    if (useRouting) {
      return {
        useRouting,
        body: {
          placement: {
            bullhorn_id: entityId,
            placement_id: entityId,
            candidate: {
              entity_type: 'candidate',
              bullhorn_id: entityInfo.candidate.bullhorn_id,
              name: entityInfo.candidate.name,
              email: candEmail,
            },
            contact: {
              entity_type: 'contact',
              bullhorn_id: entityInfo.contact.bullhorn_id,
              name: entityInfo.contact.name,
              email: contactEmail,
            },
          },
        },
      };
    }
    if (entityType === 'placement' && placementRecipientType === 'contact' && contactEmail) {
      return {
        useRouting,
        body: {
          recipient: {
            entity_type: 'placement',
            placement_recipient_type: 'contact',
            bullhorn_id: entityInfo?.contact?.bullhorn_id || entityId,
            name: entityInfo?.contact?.name || 'Client Contact',
            email: contactEmail,
            company_id: entityInfo?.company?.bullhorn_id,
            company_name: entityInfo?.company?.name,
            placement_id: entityId,
          },
        },
      };
    }
    const candBhId = entityInfo?.candidate?.bullhorn_id || entityId;
    return {
      useRouting,
      body: {
        recipient: {
          entity_type: entityType,
          placement_recipient_type: entityType === 'placement' ? 'candidate' : undefined,
          bullhorn_id: candBhId,
          name: entityInfo?.candidate?.name || entityInfo?.name || 'Unknown',
          email: recipientEmail,
          placement_id: entityType === 'placement' ? entityId : undefined,
        },
      },
    };
  };

  // Open a preview PDF from inside the embed. Two things rule out a plain
  // <a href target="_blank"> to the API, the way the main app does it:
  //  1. Auth — the embed runs in a third-party Bullhorn iframe, where the
  //     session cookie set at SSO login is routinely blocked (see the
  //     "token" query param comment in auth.ts's /sso/redirect); embedApi's
  //     calls only succeed because they carry an explicit Authorization
  //     header, which a raw link can't attach.
  //  2. Popups — window.open() from inside that same iframe is blocked
  //     outright in some hosts regardless of Bullhorn's own sandbox
  //     attributes on the tab (confirmed even on a real click during
  //     testing), so a new-tab approach can silently never work.
  // Fetching the PDF through embedApi (authenticated) and rendering the
  // resulting blob inline — via viewingPdf below — sidesteps both.
  //
  // If the Send dialog is open when this is called, close it for the
  // duration of the preview rather than leaving it mounted underneath.
  // Radix keeps a Dialog's background inert (pointer-events disabled)
  // while it's open, and the PDF viewer is a plain overlay div outside
  // Radix's own layer stack — so with the Send dialog left open, that
  // inert styling silently ate every click/scroll inside the viewer
  // (close button, zoom, page scroll, all of it — reported live). Fully
  // closing the dialog removes that conflict entirely; wasSendDialogOpenRef
  // remembers to reopen it on close so the recruiter lands back on their
  // in-progress selection, not the tab's main document list. A preview
  // triggered from OUTSIDE the dialog (the main document list) leaves it
  // false, so this never pops the dialog open where it wasn't already.
  const wasSendDialogOpenRef = useRef(false);
  const openPreviewPdf = async (url, label) => {
    try {
      const resp = await embedApi.get(url, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(resp.data);
      wasSendDialogOpenRef.current = showSendDialog;
      if (showSendDialog) setShowSendDialog(false);
      setViewingPdf({ url: blobUrl, label: label || 'Preview' });
    } catch (error) {
      toast.error('Could not open the preview PDF.');
    }
  };
  // Open the Send dialog on the admin's chosen type. A workspace without Forms can't start on one.
  const openSendDialog = () => {
    setSendMode(defaultSendMode === 'form' && !formsEnabled ? 'document' : defaultSendMode);
    setShowSendDialog(true);
  };
  const closeViewingPdf = () => {
    setViewingPdf((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    if (wasSendDialogOpenRef.current) {
      wasSendDialogOpenRef.current = false;
      setShowSendDialog(true);
    }
  };

  // Dry run: render preview PDFs for every document in the selected packet
  // without creating documents or sending anything. Always resolves full
  // placement routing (both sides) so the result can be filtered to match
  // whichever "Send to" side is currently picked — same principle as the
  // real send, so the preview never shows a document that wouldn't actually
  // go out.
  const handleTestPreviewPacket = async () => {
    if (selectedPackets.length !== 1 || !entityId) return;
    const packetId = selectedPackets[0];
    setPreviewingPacket(true);
    setPacketPreviewResults(null);
    try {
      const candEmail = entityInfo?.candidate?.email;
      const contactEmail = entityInfo?.contact?.email;
      const hasPlacementContext = entityType === 'placement' && candEmail && contactEmail;
      const { body } = hasPlacementContext
        ? { body: { placement: {
            placement_id: entityId,
            candidate: { entity_type: 'candidate', bullhorn_id: entityInfo.candidate.bullhorn_id, name: entityInfo.candidate.name, email: candEmail },
            contact: { entity_type: 'contact', bullhorn_id: entityInfo.contact.bullhorn_id, name: entityInfo.contact.name, email: contactEmail },
          } } }
        : buildPacketRoutingPayload();
      const resp = await embedApi.post('/api/packets/test-preview', { packet_id: packetId, ...body });
      const all = resp.data?.previews || [];
      // Match exactly what a real send with the current "Send to" pick would
      // include — e.g. a Candidate-only send never includes a contact-only
      // template's preview, same as templateAllowsSide at send time.
      const visible = entityType === 'placement' && placementRecipientType !== 'both'
        ? all.filter((p) => p.route === placementRecipientType)
        : all;
      setPacketPreviewResults(visible);
      toast.success(`Rendered ${visible.length} preview PDF(s)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Preview render failed');
    } finally {
      setPreviewingPacket(false);
    }
  };

  const handleSendPacket = async () => {
    if (selectedPackets.length === 0 || !entityId || !recipientEmail) return;
    if (unfilledSenderFields.length > 0) {
      toast.error(`Please fill in before sending: ${unfilledSenderFields.map((f) => f.field.label).join(', ')}`);
      return;
    }

    // Only one packet can ever be selected (see togglePacket) — one send
    // call, no per-packet accumulation.
    const packetId = selectedPackets[0];
    const packetInfo = packets.find(p => p.id === packetId);

    setSending(true);
    try {
      const { body } = buildPacketRoutingPayload();
      const prefilledForPacket = buildPrefilledValues(packetInfo?.template_ids || []);
      await embedApi.post('/api/packets/send', {
        packet_id: packetId,
        ...body,
        message: customMessage || undefined,
        expiry_hours: expiryHours,
        ...(Object.keys(prefilledForPacket).length ? { prefilled_values: prefilledForPacket } : {}),
      });

      const totalDocs = packetInfo?.template_ids?.length || 0;
      toast.success(`Packet sent! (${totalDocs} documents)`);

      setShowSendDialog(false);
      setSelectedPackets([]);
      setCustomMessage('');
      setEditingEmail(false);
      setPacketPreviewResults(null);
      loadData();

      bullhornNote.fire({
        entityType,
        entityId,
        entityName: entityInfo?.name || '',
        documentId: null,
        context: {
          template_name: packetInfo?.name || 'packet',
          document_type: `${totalDocs} document${totalDocs === 1 ? '' : 's'}`,
          form_name: null,
          recipient_name: entityInfo?.name || 'recipient',
          recipient_email: recipientEmail,
          sender_name: '',
        },
      });
    } catch (error) {
      // Handle different error formats
      const errorData = error.response?.data;
      let errorMessage = 'Failed to send packet';
      
      if (typeof errorData?.detail === 'string') {
        errorMessage = errorData.detail;
      } else if (Array.isArray(errorData?.detail)) {
        errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      } else if (errorData?.message) {
        errorMessage = errorData.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleSendForm = async () => {
    if (!selectedForm || !entityId) return;

    // Resolve recipient based on entity type — placements default to
    // candidate side here (forms can't yet be sent to both sides; if a
    // recruiter wants that, they can send the form twice, one per side
    // from the Forms workspace). For candidates/contacts we use the
    // entity's primary email.
    let recipientName, recipientAddr;
    if (entityType === 'placement') {
      recipientName = entityInfo?.candidate?.name || entityInfo?.name || 'Candidate';
      recipientAddr = recipientEmail
        || entityInfo?.candidate?.email
        || entityInfo?.email
        || '';
    } else {
      recipientName = entityInfo?.name || 'Unknown';
      recipientAddr = recipientEmail || entityInfo?.email || '';
    }

    if (!recipientAddr) {
      toast.error('This record has no email in Bullhorn — add one first.');
      return;
    }

    setSending(true);
    try {
      const form = forms.find(f => f.id === selectedForm);
      const resp = await embedApi.post('/api/forms/send', {
        form_id: selectedForm,
        bullhorn_id: String(entityId),
        entity_type: form?.entity_type || entityType,
        candidate_name: recipientName,
        candidate_email: recipientAddr,
        expiry_hours: expiryHours,
        message: customMessage || undefined,
      });

      const prefilled = resp.data?.prefilled_count || 0;
      toast.success(
        prefilled > 0
          ? `Form sent! Pre-filled ${prefilled} field${prefilled === 1 ? '' : 's'} from Bullhorn.`
          : 'Form sent successfully!'
      );
      setShowSendDialog(false);
      setSelectedForm('');
      setCustomMessage('');
      setEditingEmail(false);
      loadData();

      // Mirror the document flow — log a note against the record.
      bullhornNote.fire({
        entityType,
        entityId,
        entityName: entityInfo?.name || '',
        documentId: null, // Forms don't create a doc_id; the note is informational only
        context: {
          template_name: null,
          form_name: form?.name || 'Untitled',
          document_type: `online form "${form?.name || 'Untitled'}"`,
          recipient_name: recipientName,
          recipient_email: recipientAddr,
          sender_name: '',
        },
      });
    } catch (error) {
      const errorData = error.response?.data;
      let errorMessage = 'Failed to send form';
      if (typeof errorData?.detail === 'string') errorMessage = errorData.detail;
      else if (Array.isArray(errorData?.detail)) errorMessage = errorData.detail.map(e => e.msg || e.message).join(', ');
      else if (errorData?.message) errorMessage = errorData.message;
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const handleSend = () => {
    if (sendMode === 'document') {
      handleSendDocument();
    } else if (sendMode === 'packet') {
      handleSendPacket();
    } else if (sendMode === 'form') {
      handleSendForm();
    }
  };

  const handleCancelDocument = async (docId) => {
    try {
      await embedApi.delete(`/api/documents/${docId}`);
      toast.success('Document cancelled');
      loadData();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to cancel document';
      toast.error(errorMsg);
    }
  };

  // ── Credential Request handlers ─────────────────────────────────────
  const openCredentialDialog = async () => {
    setShowCredentialDialog(true);
    setSelectedCredentialIds([]);
    setCredentialCustomMessage('');
    setCredentialSearch('');
    setCredentialRequiredMode('all');
    if (credentialCategories.length === 0) {
      setCredentialCategoriesLoading(true);
      try {
        const r = await embedApi.get('/api/credential-requests/bullhorn-categories');
        setCredentialCategories(r.data?.categories || []);
        setQualificationsLabel(r.data?.qualifications_field?.label || null);
      } catch (e) {
        toast.error('Could not load credential categories from Bullhorn');
      } finally {
        setCredentialCategoriesLoading(false);
      }
    }
  };

  // Whose Qualifications "Pull from Qualifications" reads: this candidate, or a placement's candidate.
  // Nobody on a contact (or any other) tab, so the button simply isn't offered there.
  const qualificationsCandidateId = (() => {
    const raw = entityType === 'candidate'
      ? entityId
      : entityType === 'placement'
        ? entityInfo?.candidate?.bullhorn_id || entityInfo?.candidate?.id
        : null;
    return raw && /^\d+$/.test(String(raw)) ? String(raw) : null;
  })();

  const handleSendCredentialRequest = async () => {
    if (!recipientEmail) {
      toast.error('Recipient email is required');
      return;
    }
    if (selectedCredentialIds.length === 0) {
      toast.error('Pick at least one credential to request');
      return;
    }
    setCredentialSending(true);
    try {
      const items = selectedCredentialIds.map(id => {
        const cat = credentialCategories.find(c => c.id === id);
        return {
          bullhorn_category_id: id,
          bullhorn_category_name: cat?.name || `Credential #${id}`,
          required: credentialRequiredMode === 'all',
        };
      });

      await embedApi.post('/api/credential-requests', {
        candidate_email: recipientEmail,
        candidate_name: entityInfo?.name || null,
        candidate_bullhorn_id:
          entityType === 'candidate'
            ? parseInt(entityId, 10)
            : entityInfo?.candidate?.id || entityInfo?.candidateId || null,
        entity_type: entityType,
        entity_id: entityId ? parseInt(entityId, 10) : null,
        items,
        custom_message: credentialCustomMessage || null,
      });

      toast.success(`Credential request sent — ${items.length} item${items.length === 1 ? '' : 's'}`);
      setShowCredentialDialog(false);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send credential request');
    } finally {
      setCredentialSending(false);
    }
  };

  const handleCancelCredentialRequest = async (req) => {
    if (!window.confirm(`Cancel this credential request? The candidate's link will stop working.`)) return;
    try {
      await embedApi.post(`/api/credential-requests/${req.id}/cancel`);
      toast.success('Request cancelled');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  // Credas supports this for real (DELETE /api/v2/ci/processes/{id} on
  // their side, not just a local status flip) — see credas.ts's /cancel route.
  const handleCancelCredasRequest = async (doc) => {
    if (!window.confirm(`Cancel this Credas check? The candidate's link will stop working.`)) return;
    try {
      await embedApi.post(`/api/credas/${doc.id}/cancel`);
      toast.success('Credas check cancelled');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleRemindCredentialRequest = async (req) => {
    try {
      await embedApi.post(`/api/credential-requests/${req.id}/remind`);
      toast.success('Reminder sent');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleResendReminder = async (doc) => {
    try {
      await embedApi.post('/api/recipients/send-reminder', {
        recipient_email: doc.candidate_email,
        document_ids: [doc.id]
      });
      toast.success('Reminder sent!');
      loadData();
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Failed to send reminder';
      toast.error(errorMsg);
    }
  };

  // Re-sends the same magic link already issued, rather than creating a new
  // Credas process — see credas.ts's /:id/remind route.
  const handleRemindCredas = async (doc) => {
    try {
      await embedApi.post(`/api/credas/${doc.id}/remind`);
      toast.success('Reminder sent');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleResendCredentials = async (email) => {
    try {
      // Get tenant_id from current user context
      const meResp = await embedApi.get('/api/auth/me');
      const tId = meResp.data?.tenant_id;
      await embedApi.post('/api/portal/auth/resend-credentials', {
        email,
        tenant_id: tId,
      });
      toast.success(`New login details sent to ${email}`);
    } catch (error) {
      const errorMsg = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Failed to resend login details';
      toast.error(errorMsg);
    }
  };

  // Bulk-fire a reminder for every pending doc grouped by recipient email.
  // The backend's /api/recipients/send-reminder takes a list of document
  // IDs per call, so we batch by `candidate_email` to keep emails per
  // candidate single (one digest email instead of N).
  const handleRemindAllPending = async () => {
    const pendingDocs = documents.filter(d => ['sent', 'opened', 'in_progress'].includes(d.status));
    if (pendingDocs.length === 0) {
      toast.info('No pending documents to remind');
      return;
    }
    const grouped = pendingDocs.reduce((acc, d) => {
      const k = d.candidate_email || 'unknown';
      (acc[k] = acc[k] || []).push(d.id);
      return acc;
    }, {});
    setBulkReminding(true);
    try {
      let ok = 0;
      let failed = 0;
      for (const [email, ids] of Object.entries(grouped)) {
        if (!email || email === 'unknown') {
          failed += ids.length;
          continue;
        }
        try {
          await embedApi.post('/api/recipients/send-reminder', {
            recipient_email: email,
            document_ids: ids,
          });
          ok += ids.length;
        } catch (e) {
          failed += ids.length;
        }
      }
      if (ok > 0 && failed === 0) toast.success(`Reminder sent for ${ok} document${ok === 1 ? '' : 's'}`);
      else if (ok > 0) toast.warning(`Sent ${ok}, ${failed} failed`);
      else toast.error('All reminders failed');
      loadData();
    } finally {
      setBulkReminding(false);
    }
  };

  // Copy the candidate-facing short URL to the clipboard so the
  // recruiter can paste it into Teams / WhatsApp when the email
  // bounces or sits unread.
  const handleCopyLink = async (doc) => {
    const url = doc.share_url;
    if (!url) {
      toast.error('No share link available yet — try sending a reminder to refresh');
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers / restrictive iframes
        const ta = document.createElement('textarea');
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Link copied');
    } catch (e) {
      toast.error('Copy blocked by browser — long-press the link in a new tab');
    }
  };

  // Deep-link the recruiter into HyreDocs proper for full tracking /
  // audit. Opens in a new tab because the Bullhorn embed iframe is
  // intentionally narrow.
  const handleOpenInHyreDocs = (doc) => {
    const slug =
      (typeof window !== 'undefined' &&
        (localStorage.getItem('tenantSlug') || sessionStorage.getItem('tenantSlug'))) ||
      'default';
    const url = `${API_URL.replace(/\/$/, '')}/${slug}/documents?status=${doc.status || 'sent'}&doc=${doc.id}`;
    window.open(url, '_blank', 'noopener');
  };

  // Preview document — opens the same PDF the candidate sees in a new tab.
  // For completed docs this is the signed PDF + audit certificate. For a
  // still-outstanding document, GET .../preview-pdf renders the template
  // with whatever field values are on it RIGHT NOW (e.g. what was prefilled
  // from Bullhorn at send time) overlaid — falling back to `template_file_url`
  // instead (the previous behavior) shows the raw, unfilled template PDF,
  // which looks like "the preview is blank" for any document the candidate
  // hasn't completed yet, even when send-time prefill worked correctly.
  const handlePreview = async (doc) => {
    try {
      const { data } = await embedApi.get(`/api/documents/${doc.id}/preview-pdf`);
      if (!data?.preview_url) throw new Error('No preview available yet');
      await openPreviewPdf(data.preview_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No preview available yet');
    }
  };

  // Stats
  const stats = {
    total: documents.length,
    pending: documents.filter(d => ['sent', 'opened', 'in_progress'].includes(d.status)).length,
    completed: documents.filter(d => ['completed', 'approved', 'pending_approval'].includes(d.status)).length,
  };

  // Apply the active status-chip filter. "all" leaves the list untouched.
  // "pending" / "opened" / "completed" buckets mirror the chip labels and
  // are intentionally generous (e.g. "completed" includes pending-approval
  // and approved) so a recruiter doesn't see a chip that hides matches.
  const filteredDocuments = applyDocumentFilters(documents.filter(d => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending') return ['sent', 'in_progress'].includes(d.status);
    if (statusFilter === 'opened') return d.status === 'opened';
    if (statusFilter === 'completed') return ['completed', 'approved', 'pending_approval'].includes(d.status);
    return true;
  }), docSearch);

  // Update page title + notify Bullhorn parent window with pending count
  useEffect(() => {
    const pendingCount = stats.pending;
    const entityName = entityInfo?.name || `${entityType} #${entityId}`;
    
    // Update browser tab title
    document.title = pendingCount > 0
      ? `(${pendingCount}) ${entityName} - HyreDocs`
      : `${entityName} - HyreDocs`;
    
    // Notify parent (Bullhorn) window so it can render a badge if it listens
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({
          type: 'hyredocs:document-counts',
          entityId,
          entityType,
          total: stats.total,
          pending: stats.pending,
          completed: stats.completed,
        }, '*');
      }
    } catch (e) {
      // postMessage failures are non-fatal
    }
  }, [stats.pending, stats.total, stats.completed, entityId, entityType, entityInfo]);

  // No entity ID provided
  if (!entityId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-400" />
          <h2 className="text-lg font-semibold text-slate-700">No Entity Selected</h2>
          <p className="text-sm text-slate-500 mt-1">
            This page should be loaded with an entity ID parameter.
          </p>
          <p className="text-xs text-slate-400 mt-2 font-mono">
            ?entityId=12345&entityType=candidate
          </p>
        </div>
      </div>
    );
  }

  // Authentication is resolved in an effect after the first React render.
  // Keep the iframe neutral during that short window instead of flashing the
  // manual login form before a valid SSO/cookie token is applied.
  if (authInitializing) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="embed-auth-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // SSO failed to identify the specific Bullhorn user — surface a clear,
  // actionable "not provisioned" state rather than silently logging the
  // recruiter in as another HyreDocs user. The admin must add a HyreDocs
  // account that matches their Bullhorn email before they can proceed.
  if (ssoError === 'not_provisioned' && !authToken) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="sso-not-provisioned-screen">
        <div className="max-w-md w-full bg-white rounded-xl border border-amber-200 shadow-sm p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">HyreDocs account not found</h1>
          <p className="text-sm text-slate-600 mb-4">
            Your Bullhorn account isn&apos;t yet linked to a HyreDocs user, so we can&apos;t sign you in automatically.
          </p>
          <p className="text-sm text-slate-600 mb-6">
            Please ask your workspace admin to add your Bullhorn email to HyreDocs, then reload this tab.
          </p>
          {ssoErrorUserId && (
            <p className="text-xs text-slate-400 font-mono mb-4">Bullhorn UserID: {ssoErrorUserId}</p>
          )}
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            data-testid="sso-retry-button"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  // No auth - show inline login form
  if (!authToken) {
    return <InlineLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  // Inline PDF viewer for "Preview"/"Test preview" — rendered as a
  // fixed overlay (not window.open) since a new tab from inside the
  // Bullhorn iframe is unreliable, and not a nested Dialog since Radix
  // dialogs don't stack cleanly. blobUrl is revoked on close.
  const pdfViewerOverlay = viewingPdf && (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex flex-col"
      onClick={closeViewingPdf}
      data-testid="embed-pdf-viewer-overlay"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-2 bg-white border-b border-slate-200" onClick={(e) => e.stopPropagation()}>
        <p className="text-sm font-medium truncate">{viewingPdf.label}</p>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Some browser/profile combinations won't render a blob PDF
              inline inside an <embed> (Chrome's own PDF plugin disabled,
              "always download PDFs" turned on, etc.) and fall back to a
              bare "can't preview this file" placeholder with no way
              forward — reported live by a recruiter whose embed never
              rendered even though the exact same document opened fine
              elsewhere. This is a REAL <a>, opened by an actual click,
              so it isn't subject to the window.open()-from-an-iframe
              popup blocking noted above — always works regardless of
              whether the embed itself renders. */}
          <a
            href={viewingPdf.url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center text-xs font-medium text-slate-600 hover:text-slate-900 px-2 py-1.5 rounded-md hover:bg-slate-100"
            data-testid="embed-pdf-viewer-open-new-tab"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-1" /> Open in new tab
          </a>
          <Button variant="ghost" size="sm" onClick={closeViewingPdf} data-testid="embed-pdf-viewer-close">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>
      </div>
      <embed
        src={viewingPdf.url}
        type="application/pdf"
        title={viewingPdf.label}
        className="flex-1 w-full bg-white"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );

  if (entityType === 'company') {
    return (
      <>
        <CompanyDocumentsTab
          entityId={entityId}
          entityInfo={entityInfo}
          documents={documents}
          onRefresh={loadData}
          apiUrl={API_URL}
          onPreview={handlePreview}
          onOpenInHyreDocs={handleOpenInHyreDocs}
        />
        {pdfViewerOverlay}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                entityType === 'candidate' ? 'bg-green-100' : entityType === 'placement' ? 'bg-[#1a4a5e]/10' : 'bg-amber-100'
              }`}>
                {entityType === 'candidate' ? (
                  <User className="w-4 h-4 text-green-600" />
                ) : entityType === 'placement' ? (
                  <FileCheck className="w-4 h-4 text-[#1a4a5e]" />
                ) : (
                  <Building2 className="w-4 h-4 text-amber-600" />
                )}
              </div>
              <div>
                <h1 className="font-semibold text-sm" data-testid="embed-entity-name">
                  {entityInfo?.name || `${(ENTITY_COLORS[entityType]?.label || entityType)} #${entityId}`}
                </h1>
                {/* Show job title for placements */}
                {entityType === 'placement' && entityInfo?.job_title && (
                  <p className="text-xs text-muted-foreground">
                    {entityInfo.job_title}
                    {entityInfo.company_name && ` @ ${entityInfo.company_name}`}
                  </p>
                )}
                {entityInfo?.email && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    {entityInfo.email}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Pending count badge — red to grab attention */}
              {stats.pending > 0 && (
                <Badge className="bg-red-500 hover:bg-red-500 text-white text-xs" data-testid="embed-pending-badge">
                  {stats.pending} Pending
                </Badge>
              )}
              {/* Entity type badge */}
              <Badge 
                className={`${ENTITY_COLORS[entityType]?.bg} ${ENTITY_COLORS[entityType]?.text} text-xs`}
              >
                {ENTITY_COLORS[entityType]?.label || entityType}
              </Badge>
              <Button 
                size="sm" 
                onClick={openSendDialog}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Send className="w-4 h-4 mr-1" />
                Send
              </Button>
              <Button
                size="sm"
                onClick={openCredentialDialog}
                variant="outline"
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                data-testid="embed-request-credentials-btn"
                title="Request credentials (no PDF needed) — uploads write to the candidate's Bullhorn Credentials tab"
              >
                <ShieldPlus className="w-4 h-4 mr-1" />
                Request Credentials
              </Button>
              {credasEnabled && (entityType === 'candidate' || entityType === 'placement') && (
                <Button
                  size="sm"
                  onClick={openCredasDialog}
                  variant="outline"
                  disabled={credasSending}
                  className="border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                  data-testid="embed-send-credas-btn"
                  title="Create a Credas verification process for this candidate. Bypasses Credas's own email — HyreDocs emails the candidate the link itself, using the template in Settings → Credas."
                >
                  {credasSending ? (
                    <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Sending…</>
                  ) : (
                    <><ShieldCheck className="w-4 h-4 mr-1" /> Send to Credas</>
                  )}
                </Button>
              )}
            </div>
          </div>

          {/* No standalone Credas status badge here anymore — Credas checks
              now appear as rows in the main document list below (kind:
              'credas'), same as credential requests, with their own status
              badge, Copy link action, and (via the "ID Check" pill) visual
              distinction. Kept this a single source of truth instead of a
              second, easy-to-drift display of the same status. */}

          {/* Stats */}
          <div className="flex items-center gap-4 mt-3 text-xs">
            <span className="font-medium">{stats.total} Total</span>
            <span className="flex items-center gap-1 text-emerald-600">
              <CheckCircle2 className="w-3 h-3" />
              {stats.completed} Complete
            </span>
            <span className="flex items-center gap-1 text-amber-600">
              <Clock className="w-3 h-3" />
              {stats.pending} Pending
            </span>
            {stats.pending > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRemindAllPending}
                disabled={bulkReminding}
                className="h-6 px-2 text-xs"
                data-testid="embed-remind-all-btn"
                title={`Send a reminder email to every candidate with pending documents (${stats.pending})`}
              >
                {bulkReminding ? (
                  <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Sending…</>
                ) : (
                  <><BellRing className="w-3 h-3 mr-1" /> Remind all ({stats.pending})</>
                )}
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={loadData}
              className="ml-auto h-6 px-2"
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>

          {/* Status filter chips */}
          {documents.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3 flex-wrap" data-testid="embed-status-chips">
              {[
                { v: 'all', label: `All (${stats.total})` },
                { v: 'pending', label: `Pending (${documents.filter(d => ['sent','in_progress'].includes(d.status)).length})` },
                { v: 'opened', label: `Opened (${documents.filter(d => d.status === 'opened').length})` },
                { v: 'completed', label: `Completed (${stats.completed})` },
              ].map(chip => (
                <button
                  key={chip.v}
                  type="button"
                  onClick={() => setStatusFilter(chip.v)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                    statusFilter === chip.v
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`embed-status-chip-${chip.v}`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          )}
          {documents.length > 0 && (
            <DocumentSearchBar filters={docSearch} onChange={setDocSearch} shown={filteredDocuments.length} total={documents.length} testId="embed-search" />
          )}
        </div>
      </div>

      {/* Document List */}
      <div className="p-4 space-y-2">
        {documents.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No documents sent yet</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-4"
              onClick={openSendDialog}
            >
              <Send className="w-4 h-4 mr-2" />
              Send First Document
            </Button>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="text-center py-10" data-testid="embed-filtered-empty">
            <p className="text-sm text-slate-500">
              {(docSearch.query.trim() || docSearch.from || docSearch.to) ? 'No documents match your search.' : 'No documents match this filter.'}
            </p>
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => { setStatusFilter('all'); setDocSearch(NO_FILTERS); }}>
              Show all
            </Button>
          </div>
        ) : (
          filteredDocuments.map((doc) => {
            const isCredentialRequest = doc.kind === 'credential_request';
            const isCredas = doc.kind === 'credas';
            const outcomeBadge = isCredas ? credasOutcomeBadge(doc) : null;
            // Map a credential request's own statuses ("sent" | "opened" |
            // "in_progress" | "completed" | "cancelled") onto the same chip
            // palette the document rows use so filters & badges stay
            // consistent across both kinds.
            const status = STATUS_CONFIG[doc.status] || STATUS_CONFIG.sent;
            const StatusIcon = isCredentialRequest ? Shield : isCredas ? ShieldCheck : status.icon;
            const isExpanded = expandedDoc === doc.id;
            // A credential request candidate-side flips "sent" -> "opened" the
            // moment the link is loaded — restricting isPending to exactly
            // "sent" hid the Cancel action (and every other action in this
            // block) the instant the candidate opened it. Same pending set as
            // a document's.
            const isPending = ['sent', 'opened', 'in_progress'].includes(doc.status);

            return (
              <div 
                key={doc.id}
                className="bg-white rounded-lg border shadow-sm overflow-hidden"
              >
                <div 
                  className="p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50"
                  onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
                >
                  <div className={`w-8 h-8 rounded-lg ${status.bg} flex items-center justify-center flex-shrink-0`}>
                    <StatusIcon className={`w-4 h-4 ${status.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-medium text-sm truncate">{doc.template_name}</p>
                      {isCredentialRequest && (
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-emerald-100 text-emerald-800"
                          title="Credential upload request — writes to Bullhorn Credentials tab"
                        >
                          Credentials
                        </span>
                      )}
                      {isCredas && (
                        <span
                          className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide bg-indigo-100 text-indigo-800"
                          title="Credas ID verification check"
                        >
                          ID Check
                        </span>
                      )}
                      {/* Placement docs: show the recipient side so the
                          recruiter can see at-a-glance whether they emailed
                          the candidate or the contact for this placement. */}
                      {doc.entity_type === 'placement' && doc.placement_recipient_type && (
                        <span
                          className={`text-[10px] font-medium px-1.5 py-0.5 rounded uppercase tracking-wide ${
                            doc.placement_recipient_type === 'contact'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-green-100 text-green-800'
                          }`}
                          title={`Sent to ${doc.placement_recipient_type}`}
                        >
                          → {doc.placement_recipient_type === 'contact' ? 'Contact' : 'Candidate'}
                        </span>
                      )}
                      {doc.packet_name && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 truncate max-w-[160px]" title={`Packet: ${doc.packet_name}`} data-testid={`embed-doc-packet-${doc.id}`}>
                          {doc.packet_name}
                        </span>
                      )}
                      {entityType !== 'placement' && doc.placement_id && (
                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-50 text-sky-700" title="Sent from this placement" data-testid={`embed-doc-placement-${doc.id}`}>
                          Placement #{doc.placement_id}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {doc.sent_at ? formatDistanceToNow(new Date(doc.sent_at), { addSuffix: true }) : 'Unknown'}
                    </p>
                  </div>
                  <Badge variant="outline" className={`${status.bg} ${status.color} border-0 text-xs`}>
                    {status.label}
                  </Badge>
                  {outcomeBadge && (
                    <Badge
                      variant="outline"
                      className={`${outcomeBadge.bg} ${outcomeBadge.color} border-0 text-xs`}
                      title="Identity check outcome — distinct from the process status"
                      data-testid={`embed-verification-result-${doc.id}`}
                    >
                      {outcomeBadge.label}
                    </Badge>
                  )}
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  )}
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-3 pb-3 border-t bg-slate-50">
                    <div className="grid grid-cols-2 gap-2 py-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">Sent:</span>
                        <span className="ml-1 font-medium">
                          {doc.sent_at ? format(new Date(doc.sent_at), 'MMM d, yyyy h:mm a') : '-'}
                        </span>
                      </div>
                      {doc.opened_at && (
                        <div>
                          <span className="text-muted-foreground">Opened:</span>
                          <span className="ml-1 font-medium">
                            {format(new Date(doc.opened_at), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                      )}
                      {doc.completed_at && (
                        <div>
                          <span className="text-muted-foreground">Completed:</span>
                          <span className="ml-1 font-medium">
                            {format(new Date(doc.completed_at), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                      )}
                      {doc.expires_at && isPending && (
                        <div>
                          <span className="text-muted-foreground">Expires:</span>
                          <span className="ml-1 font-medium">
                            {format(new Date(doc.expires_at), 'MMM d, yyyy')}
                          </span>
                        </div>
                      )}
                      {/* Visible, long-press-able link — the "Copy link"
                          button above can silently fail when this page is
                          embedded in Bullhorn's iframe without clipboard-write
                          permission (clipboard API + execCommand fallback both
                          blocked). A real <a> here always works: long-press →
                          native "Copy Link", independent of any JS API. */}
                      {isPending && doc.share_url && (
                        <div className="col-span-2 min-w-0">
                          <span className="text-muted-foreground">Link:</span>
                          <a
                            href={doc.share_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="ml-1 font-mono text-[11px] text-blue-700 underline break-all"
                            data-testid={`embed-visible-link-${doc.id}`}
                          >
                            {doc.share_url}
                          </a>
                        </div>
                      )}
                      {/* Reminder schedule hint — kills the "did we chase
                          them yet?" question with one glance. */}
                      {isPending && (doc.reminder_count || doc.last_reminder_at) && (
                        <div className="col-span-2 flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1">
                          <BellRing className="w-3 h-3" />
                          <span>
                            {doc.reminder_count || 0} reminder{(doc.reminder_count || 0) === 1 ? '' : 's'} sent
                            {doc.last_reminder_at && (
                              <> · last {formatDistanceToNow(new Date(doc.last_reminder_at), { addSuffix: true })}</>
                            )}
                          </span>
                        </div>
                      )}
                      {/* Audit summary on completed docs — surfaces the
                          signature certificate and Bullhorn sync state so
                          the recruiter doesn't need to deep-link out. */}
                      {!isPending && doc.completed_at && (
                        <div className="col-span-2 flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-1">
                          <ShieldCheck className="w-3 h-3" />
                          <span>
                            Signed &amp; audited
                            {doc.bullhorn_file_attachment_id && <> · uploaded to Bullhorn Files</>}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions — a Credas row only gets Copy link: there's no
                        HyreDocs-side preview, reminder, login, tracking page,
                        or cancel action for a Credas process today. */}
                    {isPending && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        {!isCredentialRequest && !isCredas && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePreview(doc);
                            }}
                            data-testid={`embed-preview-${doc.id}`}
                          >
                            <Eye className="w-3 h-3 mr-1" />
                            Preview
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isCredas) {
                              handleRemindCredas(doc);
                            } else if (isCredentialRequest) {
                              handleRemindCredentialRequest(doc);
                            } else {
                              handleResendReminder(doc);
                            }
                          }}
                          data-testid={`embed-reminder-${doc.id}`}
                        >
                          <Mail className="w-3 h-3 mr-1" />
                          Send Reminder
                        </Button>
                        {!isCredas && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResendCredentials(doc.candidate_email);
                            }}
                            disabled={!doc.candidate_email}
                            data-testid={`embed-resend-login-${doc.id}`}
                          >
                            <KeyRound className="w-3 h-3 mr-1" />
                            Resend Login
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyLink(doc);
                          }}
                          disabled={!doc.share_url}
                          title={doc.share_url ? 'Copy the candidate completion link' : 'No share link yet — try sending a reminder'}
                          data-testid={`embed-copylink-${doc.id}`}
                        >
                          <CopyIcon className="w-3 h-3 mr-1" />
                          Copy link
                        </Button>
                        {!isCredas && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenInHyreDocs(doc);
                            }}
                            title="Open the full tracking page in HyreDocs"
                            data-testid={`embed-open-hyredocs-${doc.id}`}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Open in HyreDocs
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs text-red-600 hover:text-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isCredas) {
                              handleCancelCredasRequest(doc);
                            } else if (isCredentialRequest) {
                              handleCancelCredentialRequest(doc);
                            } else {
                              handleCancelDocument(doc.id);
                            }
                          }}
                          data-testid={`embed-cancel-${doc.id}`}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    )}

                    {/* Completed document link */}
                    {doc.completed_document_url && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`${API_URL}${doc.completed_document_url}`, '_blank');
                          }}
                          data-testid={`embed-view-completed-${doc.id}`}
                        >
                          <FileText className="w-3 h-3 mr-1" />
                          View signed PDF &amp; audit certificate
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenInHyreDocs(doc);
                          }}
                          data-testid={`embed-open-hyredocs-completed-${doc.id}`}
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          Open in HyreDocs
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Send Document/Packet Dialog */}
      <Dialog open={showSendDialog} onOpenChange={(open) => {
        setShowSendDialog(open);
        if (!open) setEditingEmail(false);
      }}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>Send to {entityInfo?.name || 'Recipient'}</span>
              {entityType && (
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${ENTITY_COLORS[entityType]?.bg || 'bg-slate-500'} ${ENTITY_COLORS[entityType]?.text || 'text-white'}`}>
                  {ENTITY_COLORS[entityType]?.label || entityType}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={sendMode} onValueChange={setSendMode} className="w-full">
            <TabsList className={`grid w-full ${formsEnabled ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <TabsTrigger value="document" className="flex items-center gap-1.5" data-testid="embed-tab-document">
                <FileText className="w-4 h-4" />
                Document
              </TabsTrigger>
              <TabsTrigger value="packet" className="flex items-center gap-1.5" data-testid="embed-tab-packet">
                <Package className="w-4 h-4" />
                Packet
              </TabsTrigger>
              {formsEnabled && (
                <TabsTrigger value="form" className="flex items-center gap-1.5" data-testid="embed-tab-form">
                  <ClipboardEdit className="w-4 h-4" />
                  Form
                </TabsTrigger>
              )}
            </TabsList>

            {/* ── Document picker ────────────────────────────────────── */}
            <TabsContent value="document" className="space-y-3 mt-4">
              {(() => {
                const eligible = templates
                  .filter(t => t.entity_type === entityType || !t.entity_type || t.entity_type === '')
                  .filter(t => !templateSearch || (t.name || '').toLowerCase().includes(templateSearch.toLowerCase()))
                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                return (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <Label className="text-sm font-medium flex-shrink-0">Select Template</Label>
                      <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                          placeholder="Search templates…"
                          value={templateSearch}
                          onChange={(e) => setTemplateSearch(e.target.value)}
                          className="pl-8 h-8 text-sm"
                          data-testid="embed-template-search"
                        />
                      </div>
                    </div>

                    {eligible.length === 0 ? (
                      <div className="text-center py-8 px-4 border border-dashed border-slate-200 rounded-lg">
                        <FileText className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-600">
                          {templateSearch
                            ? `No templates match "${templateSearch}".`
                            : `No templates available for ${entityType}s.`}
                        </p>
                        {!templateSearch && (
                          <p className="text-xs text-amber-600 mt-1">
                            Create a {entityType} template in Templates page.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="max-h-[42vh] overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white" data-testid="embed-template-list">
                        {eligible.map((t) => {
                          // Determine badge based on routing flags, not entity_type
                          const isContactOnly = !!t.send_to_contact && !t.send_to_candidate;
                          const isCandidateOnly = !!t.send_to_candidate && !t.send_to_contact;
                          const ec = isContactOnly
                            ? ENTITY_COLORS.contact
                            : isCandidateOnly
                              ? ENTITY_COLORS.candidate
                              : ENTITY_COLORS[t.entity_type || entityType] || ENTITY_COLORS.candidate;
                          const isSelected = selectedTemplates.includes(t.id);
                          return (
                            <button
                              key={t.id}
                              type="button"
                              onClick={() => toggleTemplate(t.id)}
                              className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-3 overflow-hidden ${isSelected ? 'bg-slate-50' : ''}`}
                              data-testid={`embed-template-${t.id}`}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>
                                {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="font-medium text-sm truncate min-w-0">{t.name}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${templateTypeTagClass(t)} flex-shrink-0 whitespace-nowrap`}>
                                    {templateTypeLabel(t)}
                                  </span>
                                  {t.is_informational && (
                                    <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200 flex-shrink-0 whitespace-nowrap" title="Read-only · no required fields">
                                      <Eye className="w-2.5 h-2.5" />
                                      Info
                                    </span>
                                  )}
                                </div>
                                {/* Meta line — field count only. The full
                                    candidate-facing instructions live on the
                                    template itself; surfacing them here was
                                    forcing horizontal overflow inside the
                                    narrow Bullhorn iframe and offered no
                                    value to the recruiter who's only
                                    deciding *which* template to send. */}
                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                  {t.fields?.length || 0} fields
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <p className="text-[11px] text-muted-foreground">
                        {eligible.length} template{eligible.length === 1 ? '' : 's'} available
                        {selectedTemplates.length > 0 && ` · ${selectedTemplates.length} selected`}
                      </p>
                      {selectedTemplates.length > 0 && (
                        <button type="button" onClick={() => { setSelectedTemplates([]); setDocumentPreview(null); setDocumentPreviewValues({}); }} className="text-[11px] text-red-500 hover:text-red-700 font-medium">
                          Uncheck All
                        </button>
                      )}
                    </div>

                    {/* Placement-only: show exactly who each selected
                        document will go to given the current "Send to"
                        choice, so a mixed candidate/contact selection is
                        never a surprise — mirrors the Packet tab's own
                        routing preview below and the actual send-time
                        filtering (templateAllowsSide). */}
                    {entityType === 'placement' && selectedTemplates.length > 0 && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="embed-document-routing-preview">
                        <p className="text-xs font-medium text-slate-700 flex items-center gap-1.5 mb-2">
                          <Layers className="w-3.5 h-3.5" />
                          Selected documents:
                        </p>
                        <ul className="space-y-1">
                          {templates.filter((t) => selectedTemplates.includes(t.id)).map((t) => {
                            const { sendToCandidate, sendToContact } = templateRouting(t);
                            const goesTo = placementRecipientType === 'both'
                              ? [sendToCandidate && 'Candidate', sendToContact && 'Contact'].filter(Boolean)
                              : templateAllowsSide(t, placementRecipientType)
                                ? [placementRecipientType === 'contact' ? 'Contact' : 'Candidate']
                                : [];
                            const skipped = goesTo.length === 0;
                            return (
                              <li key={t.id} className="flex items-center gap-1.5 text-xs">
                                {skipped ? (
                                  <AlertCircle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                                ) : goesTo.includes('Contact') && goesTo.length === 1 ? (
                                  <Building2 className="w-3 h-3 text-[#F9A825] flex-shrink-0" />
                                ) : (
                                  <User className="w-3 h-3 text-[#4CAF50] flex-shrink-0" />
                                )}
                                <span className={`truncate ${skipped ? 'text-slate-400' : 'text-slate-600'}`}>{t.name}</span>
                                <span className={`ml-auto flex-shrink-0 ${skipped ? 'text-amber-600' : 'text-slate-400'}`}>
                                  {skipped ? 'not sent' : `→ ${goesTo.join(' + ')}`}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Dry-run preview — rendered PDF (opens in a new tab,
                        same as the main app's) plus the resolved Bullhorn
                        values in an editable list. Edits here are what
                        actually gets sent (see prefilledForTemplate in
                        handleSendDocument). */}
                    {documentPreview && documentPreview.templateId === selectedTemplates[0] && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 space-y-3" data-testid="embed-document-preview-panel">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                            <Eye className="w-3.5 h-3.5 text-emerald-600" />
                            Preview — {documentPreview.prefilled_count}/{documentPreview.total_field_count} fields prefilled
                          </p>
                          <div className="flex items-center gap-2">
                            {documentPreview.preview_url && (
                              <Button variant="outline" size="icon" className="h-7 w-7" onClick={() => openPreviewPdf(documentPreview.preview_url, templates.find((t) => t.id === documentPreview.templateId)?.name)} title="Open PDF" aria-label="Open PDF">
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            <button type="button" onClick={() => { setDocumentPreview(null); setDocumentPreviewValues({}); }} className="text-[11px] text-muted-foreground hover:text-foreground">
                              Dismiss
                            </button>
                          </div>
                        </div>
                        {documentPreview.preview_html != null && (
                          <div className="space-y-1" data-testid="embed-text-template-preview">
                            <p className="text-[11px] text-slate-600">The contract as the {entityType === 'contact' ? 'contact' : 'candidate'} will see it:</p>
                            <div
                              className={`bg-white border border-slate-200 rounded-md p-3 max-h-[28vh] overflow-y-auto contract-doc !text-xs ${FILLED_FIELD_CLASSES}`}
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(withSignaturePlaceholder(documentPreview.preview_html, entityType === 'contact' ? 'contact' : 'candidate')) }}
                            />
                          </div>
                        )}
                        {documentPreview.fields?.length > 0 && (
                          <div className="space-y-2 max-h-[30vh] overflow-y-auto pr-1">
                            {documentPreview.fields
                              // Text templates: the sender's own fields are filled in above,
                              // and a date that is stamped at signing has nothing to enter.
                              .filter((f) => !(documentPreview.preview_html != null && (f.source === 'manual' || f.auto_pending)))
                              .map((f) => {
                                const blank = documentPreview.preview_html != null && !f.auto_pending
                                  && String(documentPreviewValues[f.field_id] ?? '').trim() === '';
                                return (
                                  <div key={f.field_id} className="space-y-1">
                                    <Label className="text-[11px] text-slate-600">
                                      {f.label}
                                      {blank && <span className="text-red-600 ml-1">— required, not in Bullhorn</span>}
                                    </Label>
                                    <Input
                                      value={documentPreviewValues[f.field_id] ?? ''}
                                      onChange={(e) => setDocumentPreviewValues((prev) => ({ ...prev, [f.field_id]: e.target.value }))}
                                      className={`h-7 text-xs ${blank ? 'border-red-400' : ''}`}
                                      data-testid={`embed-preview-value-${f.field_id}`}
                                    />
                                  </div>
                                );
                              })}
                          </div>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full h-7 text-xs"
                          disabled={previewingDocument}
                          onClick={handleTestPreviewDocument}
                        >
                          {previewingDocument ? <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1.5" />}
                          Re-render with edited values
                        </Button>
                      </div>
                    )}

                    {/* Dry-run PDFs for 2+ selected documents — no editable
                        panel (see handleTestPreviewDocuments), just prefill
                        stats and an Open PDF link per template. */}
                    {documentsPreviewResults && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="embed-documents-preview-panel">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                            <Eye className="w-3.5 h-3.5 text-emerald-600" />
                            Test preview results
                          </p>
                          <button type="button" onClick={() => setDocumentsPreviewResults(null)} className="text-[11px] text-muted-foreground hover:text-foreground">
                            Dismiss
                          </button>
                        </div>
                        <ul className="space-y-1.5">
                          {documentsPreviewResults.map((p) => (
                            <li key={p.templateId} className="flex items-center justify-between gap-2 text-xs bg-white rounded-md border border-slate-200 px-2.5 py-1.5 min-w-0">
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-slate-700 truncate">{p.templateName}</p>
                                {!p.skipped && !p.error && (
                                  <p className="text-[11px] text-muted-foreground truncate">{p.prefilled_count}/{p.total_field_count} fields prefilled</p>
                                )}
                              </div>
                              {p.skipped ? (
                                <span className="text-[11px] text-amber-600 flex-shrink-0">Not applicable to current Send to</span>
                              ) : p.preview_url ? (
                                <Button variant="outline" size="icon" className="h-6 w-6 flex-shrink-0" onClick={() => openPreviewPdf(p.preview_url, p.templateName)} title="Open PDF" aria-label="Open PDF">
                                  <Eye className="w-3.5 h-3.5" />
                                </Button>
                              ) : (
                                <span className="text-[11px] text-red-600 flex-shrink-0">Render failed</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                );
              })()}
            </TabsContent>

            {/* ── Packet picker ──────────────────────────────────────── */}
            <TabsContent value="packet" className="space-y-3 mt-4">
              {(() => {
                const eligible = packets
                  .filter(p => p.entity_type === entityType || !p.entity_type || p.entity_type === '')
                  .filter(p => !packetSearch || (p.name || '').toLowerCase().includes(packetSearch.toLowerCase()))
                  .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                const selectedPacketObj = selectedPackets.length === 1 ? packets.find(p => p.id === selectedPackets[0]) : null;
                return (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <Label className="text-sm font-medium flex-shrink-0">Select Packet</Label>
                      <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <Input
                          placeholder="Search packets…"
                          value={packetSearch}
                          onChange={(e) => setPacketSearch(e.target.value)}
                          className="pl-8 h-8 text-sm"
                          data-testid="embed-packet-search"
                        />
                      </div>
                    </div>

                    {eligible.length === 0 ? (
                      <div className="text-center py-8 px-4 border border-dashed border-slate-200 rounded-lg">
                        <Package className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm text-slate-600">
                          {packetSearch ? `No packets match "${packetSearch}".` : 'No packets available for this entity type.'}
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-[36vh] overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white" data-testid="embed-packet-list">
                        {eligible.map((p) => {
                          const ec = ENTITY_COLORS[p.entity_type || entityType] || ENTITY_COLORS.candidate;
                          const isSelected = selectedPackets.includes(p.id);
                          const docCount = p.template_ids?.length || 0;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => togglePacket(p.id)}
                              className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-3 ${isSelected ? 'bg-slate-50' : ''}`}
                              data-testid={`embed-packet-${p.id}`}
                            >
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>
                                {isSelected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm truncate">{p.name}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${ec.bg} ${ec.text} flex-shrink-0 whitespace-nowrap`}>
                                    {ec.label}
                                  </span>
                                </div>
                                {/* One document per line rather than a single
                                    comma-joined summary — a long packet with
                                    several documents (typical for a
                                    Placement packet) made that summary run
                                    off the edge of Bullhorn's fixed-width
                                    embed iframe, which has no horizontal
                                    scroll, so the overflow was simply
                                    invisible no matter how well it truncated. */}
                                <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                                  <Files className="w-3 h-3 flex-shrink-0" />
                                  {docCount} doc{docCount !== 1 ? 's' : ''}
                                </div>
                                {formatPacketEstimate(p.templates) && (
                                  /* Own line, not appended to the count: the embed iframe
                                     can't scroll sideways, so a long phrase must wrap. */
                                  <div className="text-[11px] text-muted-foreground mt-0.5 flex items-start gap-1" data-testid={`embed-packet-estimate-${p.id}`}>
                                    <Timer className="w-3 h-3 flex-shrink-0 mt-px" />
                                    <span className="min-w-0 break-words">{formatPacketEstimate(p.templates)}</span>
                                  </div>
                                )}
                                {p.templates && p.templates.length > 0 && (
                                  <div className="mt-0.5 space-y-0.5">
                                    {p.templates.slice(0, 4).map((t) => (
                                      <div key={t.id} className="text-[11px] text-muted-foreground truncate pl-4">
                                        &middot; {t.name}
                                      </div>
                                    ))}
                                    {p.templates.length > 4 && (
                                      <div className="text-[11px] text-slate-400 pl-4">+{p.templates.length - 4} more</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {selectedPackets.length > 0 && (
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] text-muted-foreground">
                          1 packet selected
                        </p>
                        <button type="button" onClick={() => { setSelectedPackets([]); setPacketPreviewResults(null); }} className="text-[11px] text-red-500 hover:text-red-700 font-medium">
                          Uncheck
                        </button>
                      </div>
                    )}

                    {selectedPacketObj && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <p className="text-xs font-medium text-slate-700 flex items-center gap-1.5 mb-2">
                          <Layers className="w-3.5 h-3.5" />
                          Documents in &ldquo;{selectedPacketObj.name}&rdquo;:
                        </p>
                        <ul className="space-y-1">
                          {selectedPacketObj.templates?.map((t, i) => {
                            // Placement-only: show exactly who this document
                            // will go to given the current "Send to" choice,
                            // so a mixed candidate/contact packet is never a
                            // surprise — mirrors the main app's routing
                            // preview (SendPacketPage.js) and the actual
                            // send-time filtering (templateAllowsSide).
                            if (entityType !== 'placement') {
                              return (
                                <li key={t.id || i} className="flex items-center gap-1.5 text-xs text-slate-600 min-w-0">
                                  <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                  <span className="truncate min-w-0 flex-1">{t.name}</span>
                                </li>
                              );
                            }
                            const { sendToCandidate, sendToContact } = templateRouting(t);
                            const goesTo = placementRecipientType === 'both'
                              ? [sendToCandidate && 'Candidate', sendToContact && 'Contact'].filter(Boolean)
                              : templateAllowsSide(t, placementRecipientType)
                                ? [placementRecipientType === 'contact' ? 'Contact' : 'Candidate']
                                : [];
                            const skipped = goesTo.length === 0;
                            return (
                              <li key={t.id || i} className="flex items-center gap-1.5 text-xs min-w-0">
                                {skipped ? (
                                  <AlertCircle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                                ) : goesTo.includes('Contact') && goesTo.length === 1 ? (
                                  <Building2 className="w-3 h-3 text-[#F9A825] flex-shrink-0" />
                                ) : (
                                  <User className="w-3 h-3 text-[#4CAF50] flex-shrink-0" />
                                )}
                                <span className={`truncate min-w-0 flex-1 ${skipped ? 'text-slate-400' : 'text-slate-600'}`}>{t.name}</span>
                                <span className={`ml-auto flex-shrink-0 ${skipped ? 'text-amber-600' : 'text-slate-400'}`}>
                                  {skipped ? 'not sent' : `→ ${goesTo.join(' + ')}`}
                                </span>
                              </li>
                            );
                          }) || (
                            <li className="text-xs text-slate-400">
                              {selectedPacketObj.template_ids?.length || 0} document(s)
                            </li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Dry-run PDFs for every document that would actually go
                        out — no documents created, no emails sent. Mirrors
                        SendPacketPage.js's results panel. */}
                    {packetPreviewResults && (
                      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="embed-packet-preview-panel">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-slate-700 flex items-center gap-1.5">
                            <Eye className="w-3.5 h-3.5 text-emerald-600" />
                            Test preview results
                          </p>
                          <button type="button" onClick={() => setPacketPreviewResults(null)} className="text-[11px] text-muted-foreground hover:text-foreground">
                            Dismiss
                          </button>
                        </div>
                        {packetPreviewResults.length === 0 ? (
                          <p className="text-xs text-amber-600">No documents match the current "Send to" selection.</p>
                        ) : (
                          <ul className="space-y-1.5">
                            {packetPreviewResults.map((p) => (
                              <li key={p.template_id} className="flex items-center justify-between gap-2 text-xs bg-white rounded-md border border-slate-200 px-2.5 py-1.5 min-w-0">
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-slate-700 truncate">{p.template_name}</p>
                                  <p className="text-[11px] text-muted-foreground capitalize truncate">{p.route} · {p.prefilled_count}/{p.total_field_count} fields prefilled</p>
                                </div>
                                {p.preview_url ? (
                                  <Button variant="outline" size="icon" className="h-6 w-6 flex-shrink-0" onClick={() => openPreviewPdf(p.preview_url, p.template_name)} title="Open PDF" aria-label="Open PDF">
                                    <Eye className="w-3.5 h-3.5" />
                                  </Button>
                                ) : (
                                  <span className="text-[11px] text-red-600 flex-shrink-0">Render failed</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
            </TabsContent>

            {/* ── Form picker ────────────────────────────────────────── */}
            {formsEnabled && (
              <TabsContent value="form" className="space-y-3 mt-4" data-testid="embed-form-tab-content">
                {(() => {
                  const eligible = (forms || [])
                    .filter(f => !formSearch || (f.name || '').toLowerCase().includes(formSearch.toLowerCase()))
                    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                  const selectedFormObj = forms.find(x => x.id === selectedForm);
                  const mappedCount = selectedFormObj
                    ? (selectedFormObj.fields || []).filter(fd => fd.bullhorn_source_field && fd.prefill_from_bullhorn !== false).length
                    : 0;
                  return (
                    <>
                      <div className="flex items-center justify-between gap-3">
                        <Label className="text-sm font-medium flex-shrink-0">Select Form</Label>
                        <div className="relative flex-1 max-w-xs">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                          <Input
                            placeholder="Search forms…"
                            value={formSearch}
                            onChange={(e) => setFormSearch(e.target.value)}
                            className="pl-8 h-8 text-sm"
                            data-testid="embed-form-search"
                          />
                        </div>
                      </div>

                      {eligible.length === 0 ? (
                        <div className="text-center py-8 px-4 border border-dashed border-slate-200 rounded-lg">
                          <ClipboardEdit className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                          <p className="text-sm text-slate-600">
                            {formSearch ? `No forms match "${formSearch}".` : `No forms available for ${entityType}s.`}
                          </p>
                          {!formSearch && (
                            <p className="text-xs text-amber-600 mt-1">
                              Create one from the Forms workspace.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="max-h-[36vh] overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white" data-testid="embed-form-list">
                          {eligible.map((f) => {
                            const isSelected = selectedForm === f.id;
                            return (
                              <button
                                key={f.id}
                                type="button"
                                onClick={() => setSelectedForm(f.id)}
                                className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 transition-colors flex items-center gap-3 ${isSelected ? 'bg-slate-50' : ''}`}
                                data-testid={`embed-form-${f.id}`}
                              >
                                <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-violet-600' : 'bg-violet-100'}`}>
                                  <ClipboardEdit className={`w-3.5 h-3.5 ${isSelected ? 'text-white' : 'text-violet-600'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-sm truncate">{f.name}</span>
                                    {f.status === 'draft' && (
                                      <Badge variant="secondary" className="text-[10px] flex-shrink-0">Draft</Badge>
                                    )}
                                    <Badge variant="outline" className="text-[10px] flex-shrink-0">
                                      {f.fields?.length || 0} field{f.fields?.length === 1 ? '' : 's'}
                                    </Badge>
                                  </div>
                                  {f.description && (
                                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{f.description}</div>
                                  )}
                                </div>
                                {isSelected && <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {selectedFormObj && (
                        <div className="bg-violet-50 border border-violet-200 rounded-lg p-3" data-testid="embed-form-preview">
                          <p className="font-medium text-violet-900 flex items-center gap-1.5 text-sm">
                            <ClipboardEdit className="w-4 h-4" />
                            {selectedFormObj.name}
                          </p>
                          <p className="text-xs text-violet-700 mt-1.5">
                            {mappedCount > 0
                              ? `${mappedCount} field${mappedCount === 1 ? '' : 's'} will be pre-filled from this Bullhorn record.`
                              : 'No mapped fields — candidate will fill everything from scratch.'}
                          </p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </TabsContent>
            )}
          </Tabs>

          <div className="space-y-4">
            {/* Placement-only: Candidate / Contact / Both recipient toggle */}
            {entityType === 'placement' && entityInfo && (() => {
              const candEmail = entityInfo.candidate?.email || entityInfo.email || '';
              const candName = entityInfo.candidate?.name || entityInfo.name || '';
              const contactEmail = entityInfo.contact?.email || '';
              const contactName = entityInfo.contact?.name || '';
              const hasContact = !!(contactEmail || contactName);
              return (
                <div className="space-y-2" data-testid="embed-placement-recipient-toggle">
                  <Label>Send to</Label>
                  <div className="grid grid-cols-3 gap-1.5 bg-slate-100 rounded-lg p-1">
                    <button
                      type="button"
                      onClick={() => setPlacementRecipientType('candidate')}
                      disabled={!canSendToCandidateSide}
                      className={`text-xs px-2 py-2 rounded-md transition-colors text-left ${
                        placementRecipientType === 'candidate'
                          ? 'bg-[#1a4a5e] text-white shadow-sm'
                          : canSendToCandidateSide
                            ? 'text-slate-700 hover:bg-white'
                            : 'text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="embed-recipient-candidate"
                      title={canSendToCandidateSide ? undefined : 'The selected document(s) are not set up to send to the candidate'}
                    >
                      <div className="font-medium">Candidate</div>
                      <div className={`text-[10px] truncate ${placementRecipientType === 'candidate' ? 'text-white/80' : 'text-slate-500'}`} title={candName}>
                        {candName || '—'}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPlacementRecipientType('contact')}
                      disabled={!hasContact || !canSendToContactSide}
                      className={`text-xs px-2 py-2 rounded-md transition-colors text-left ${
                        placementRecipientType === 'contact'
                          ? 'bg-[#1a4a5e] text-white shadow-sm'
                          : hasContact && canSendToContactSide
                            ? 'text-slate-700 hover:bg-white'
                            : 'text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="embed-recipient-contact"
                      title={
                        !hasContact ? 'No client contact linked to this placement'
                          : !canSendToContactSide ? 'The selected document(s) are not set up to send to the client contact'
                            : `Send to client contact: ${contactName}`
                      }
                    >
                      <div className="font-medium">Contact</div>
                      <div className={`text-[10px] truncate ${placementRecipientType === 'contact' ? 'text-white/80' : 'text-slate-500'}`} title={contactName}>
                        {contactName || 'Not linked'}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPlacementRecipientType('both')}
                      disabled={!hasContact || !candEmail || !canSendToBothSides}
                      className={`text-xs px-2 py-2 rounded-md transition-colors text-left ${
                        placementRecipientType === 'both'
                          ? 'bg-[#1a4a5e] text-white shadow-sm'
                          : hasContact && candEmail && canSendToBothSides
                            ? 'text-slate-700 hover:bg-white'
                            : 'text-slate-400 cursor-not-allowed'
                      }`}
                      data-testid="embed-recipient-both"
                      title={
                        !canSendToBothSides
                          ? 'One or more selected documents only go to one side — Candidate or Contact, not both'
                          : 'Send one document to each — candidate and contact'
                      }
                    >
                      <div className="font-medium">Both</div>
                      <div className={`text-[10px] ${placementRecipientType === 'both' ? 'text-white/80' : 'text-slate-500'}`}>
                        Candidate + Contact
                      </div>
                    </button>
                  </div>
                </div>
              );
            })()}

            {(() => {
              // When sending to "both" on a placement, skip the single-
              // email editor — we use both Bullhorn emails directly and
              // showing one editable input is confusing.
              if (entityType === 'placement' && placementRecipientType === 'both') {
                const candEmail = entityInfo?.candidate?.email || entityInfo?.email || '';
                const contactEmail = entityInfo?.contact?.email || '';
                return (
                  <div className="space-y-2">
                    <Label>Recipients</Label>
                    <div className="rounded-lg border bg-emerald-50 border-emerald-200 px-3 py-2.5 text-sm space-y-1.5" data-testid="embed-both-recipients-summary">
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
                        <span className="font-medium text-emerald-900 truncate">{candEmail || '(no candidate email)'}</span>
                        <span className="text-[10px] text-emerald-700 ml-auto bg-emerald-100 px-1.5 py-0.5 rounded">Candidate</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
                        <span className="font-medium text-emerald-900 truncate">{contactEmail || '(no contact email)'}</span>
                        <span className="text-[10px] text-emerald-700 ml-auto bg-emerald-100 px-1.5 py-0.5 rounded">Contact</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground">One document will be sent to each — the candidate gets the worker-facing copy, the contact gets the client-facing copy.</p>
                  </div>
                );
              }

              // When Bullhorn gives us a primary email for the record, default to
              // sending there with a tiny "use a different email" toggle. The
              // recruiter never has to type it.
              const entityEmail = entityType === 'placement' && placementRecipientType === 'contact'
                ? (entityInfo?.contact?.email || '')
                : (entityInfo?.candidate?.email || entityInfo?.email || '');
              // Same side-aware resolution as entityEmail — this is who "Sending
              // to" further down used to always show the candidate for,
              // regardless of the actual Candidate/Contact/Both choice.
              const entityName = entityType === 'placement' && placementRecipientType === 'contact'
                ? (entityInfo?.contact?.name || '')
                : (entityInfo?.candidate?.name || entityInfo?.name || '');
              const usingEntityEmail = entityEmail && recipientEmail.toLowerCase() === entityEmail.toLowerCase();
              const showEditor = editingEmail || !entityEmail;

              if (!showEditor) {
                return (
                  <div className="space-y-2">
                    <Label>Recipient Email</Label>
                    <div className="flex items-center gap-2 rounded-lg border bg-emerald-50 border-emerald-200 px-3 py-2.5 text-sm" data-testid="embed-auto-email-row">
                      <Mail className="w-4 h-4 text-emerald-700 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        {entityName && (
                          <p className="font-medium text-emerald-900 truncate" data-testid="embed-auto-email-name">
                            {entityName}
                          </p>
                        )}
                        <p className="font-medium text-emerald-900 truncate" data-testid="embed-auto-email-value">
                          {entityEmail}
                        </p>
                        <p className="text-xs text-emerald-700">
                          Pulled from {entityType === 'placement'
                            ? (placementRecipientType === 'contact' ? "client contact's primary email" : "candidate's primary email")
                            : 'primary email on Bullhorn record'}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 flex-shrink-0"
                        onClick={() => setEditingEmail(true)}
                        data-testid="embed-change-email-btn"
                      >
                        Change
                      </Button>
                    </div>
                  </div>
                );
              }

              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Recipient Email <span className="text-red-500">*</span></Label>
                    {entityEmail && !usingEntityEmail && (
                      <button
                        type="button"
                        className="text-xs text-emerald-700 hover:underline"
                        onClick={() => { setRecipientEmail(entityEmail); setEditingEmail(false); }}
                        data-testid="embed-reset-email-btn"
                      >
                        Use Bullhorn email
                      </button>
                    )}
                  </div>
                  <Input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="candidate@email.com"
                    required
                    data-testid="embed-recipient-email-input"
                  />
                  {!recipientEmail && (
                    <p className="text-xs text-amber-600">Email is required to send documents</p>
                  )}
                </div>
              );
            })()}

            {(sendMode === 'document' || sendMode === 'packet') && senderFillableFields.length > 0 && (
              <div className="space-y-2 bg-violet-50 border border-violet-200 rounded-lg p-3" data-testid="embed-sender-fillable-section">
                <p className="text-xs font-semibold text-violet-700 flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Requester fields — required before sending
                </p>
                {senderFillableFields.map(({ templateId, templateName, field }) => {
                  const key = senderFieldKey(templateId, field.id);
                  const value = senderFieldValues[key];
                  return (
                    <div key={key} className="space-y-1">
                      <Label className="text-xs">
                        {field.label}
                        <span className="text-muted-foreground font-normal"> · {templateName}</span>
                      </Label>
                      {field.field_type === 'signature' ? (
                        value ? (
                          <div className="flex items-center gap-2 p-1.5 border border-violet-200 rounded bg-white">
                            <img src={value} alt="Signature" className="h-8 max-w-[120px] object-contain" />
                            <Button type="button" size="sm" variant="ghost" className="h-6 text-[10px] px-2" onClick={() => setSigningKey(key)}>
                              Re-sign
                            </Button>
                          </div>
                        ) : (
                          <Button
                            type="button" size="sm" variant="outline"
                            className="w-full h-7 text-xs gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-100"
                            onClick={() => setSigningKey(key)}
                            data-testid={`embed-sign-now-${field.id}`}
                          >
                            <PenTool className="w-3 h-3" /> Sign now
                          </Button>
                        )
                      ) : (
                        <Input
                          value={value || ''}
                          onChange={(e) => setSenderFieldValues(prev => ({ ...prev, [key]: e.target.value }))}
                          placeholder={`Enter ${field.label.toLowerCase()}`}
                          className="h-7 text-xs"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="space-y-2">
              <Label>Link Expiry</Label>
              <Select value={String(expiryHours)} onValueChange={(v) => setExpiryHours(parseInt(v, 10))}>
                <SelectTrigger data-testid="send-expiry-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {linkExpiryOptions(expiryHours).map((o) => (
                    <SelectItem key={o.hours} value={String(o.hours)}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Custom Message (optional)</Label>
              <Textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Add a personal message to the email..."
                rows={3}
              />
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setShowSendDialog(false); setDocumentPreview(null); setDocumentPreviewValues({}); setDocumentsPreviewResults(null); setPacketPreviewResults(null); }}>
                Cancel
              </Button>
              {sendMode === 'document' && selectedTemplates.length === 1 && (
                <Button
                  variant="outline"
                  onClick={handleTestPreviewDocument}
                  disabled={previewingDocument || unfilledSenderFields.length > 0}
                  title={unfilledSenderFields.length > 0 ? 'Fill in the required fields above before previewing' : undefined}
                  data-testid="embed-preview-document-btn"
                >
                  {previewingDocument ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                  Preview
                </Button>
              )}
              {sendMode === 'document' && selectedTemplates.length > 1 && (
                <Button
                  variant="outline"
                  onClick={handleTestPreviewDocuments}
                  disabled={previewingDocuments}
                  data-testid="embed-preview-documents-btn"
                >
                  {previewingDocuments ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                  Test preview
                </Button>
              )}
              {sendMode === 'packet' && selectedPackets.length === 1 && (
                <Button
                  variant="outline"
                  onClick={handleTestPreviewPacket}
                  disabled={previewingPacket}
                  data-testid="embed-preview-packet-btn"
                >
                  {previewingPacket ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                  Test preview
                </Button>
              )}
              <Button
                onClick={handleSend}
                disabled={
                  (sendMode === 'document' && selectedTemplates.length === 0) ||
                  (sendMode === 'packet' && selectedPackets.length === 0) ||
                  (sendMode === 'form' && !selectedForm) ||
                  ((sendMode === 'document' || sendMode === 'packet') && unfilledSenderFields.length > 0) ||
                  (sendMode === 'document' && textTemplateSelected &&
                    (selectedTemplates.length > 1 || textTemplateBlank === null || textTemplateBlank.length > 0)) ||
                  !recipientEmail ||
                  sending
                }
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="embed-send-btn"
              >
                {sending ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {sendMode === 'document' && selectedTemplates.length > 1
                  ? `Send ${selectedTemplates.length} Documents`
                  : `Send ${sendMode === 'packet' ? 'Packet' : sendMode === 'form' ? 'Form' : 'Document'}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {pdfViewerOverlay}

      <BullhornNotePromptDialog
        {...bullhornNote.popup}
        apiClient={embedApi}
      />

      {/* Requester/Admin signature capture */}
      <SignatureCaptureDialog
        open={!!signingKey}
        onOpenChange={(open) => !open && setSigningKey(null)}
        mode={signatureMode}
        onModeChange={setSignatureMode}
        typedValue={typedSignature}
        onTypedValueChange={setTypedSignature}
        canvasRef={initSignatureCanvas}
        onClearCanvas={() => signaturePadRef.current?.clear()}
        onCancel={() => setSigningKey(null)}
        onSave={saveSignature}
        testIds={{ dialog: 'embed-signature-dialog', canvas: 'embed-signature-canvas', save: 'embed-signature-save-btn' }}
      />

      {/* Credential Request dialog */}
      <Dialog open={showCredentialDialog} onOpenChange={setShowCredentialDialog}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-600" />
              Request Credentials
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Pick the credentials this candidate needs to upload. Each item will land on their Bullhorn Credentials tab.
              No PDF needed — they get a streamlined upload page.
            </p>
          </DialogHeader>

          <div className="space-y-4 pt-2 min-w-0">
            {/* Recipient */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Send to</Label>
              <Input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="candidate@example.com"
                data-testid="credreq-recipient-email"
              />
              {entityInfo?.name && (
                <p className="text-xs text-muted-foreground">{entityInfo.name}</p>
              )}
            </div>

            {/* Required mode */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Items are…</Label>
              <div className="flex gap-2">
                {[
                  { v: 'all', label: 'All required' },
                  { v: 'optional', label: 'All optional' },
                ].map(opt => (
                  <Button
                    key={opt.v}
                    type="button"
                    size="sm"
                    variant={credentialRequiredMode === opt.v ? 'default' : 'outline'}
                    onClick={() => setCredentialRequiredMode(opt.v)}
                    data-testid={`credreq-required-${opt.v}`}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Category picker */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Credentials ({selectedCredentialIds.length} selected)
                </Label>
                {selectedCredentialIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedCredentialIds([])}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    Clear
                  </button>
                )}
              </div>
              {qualificationsCandidateId && (
                <PullQualificationsButton
                  candidateId={qualificationsCandidateId}
                  categories={credentialCategories}
                  selectedIds={selectedCredentialIds}
                  onSelectIds={setSelectedCredentialIds}
                  label={qualificationsLabel}
                  fetchQualifications={(id) => embedApi.get(`/api/credential-requests/candidate-qualifications/${id}`).then((r) => r.data)}
                />
              )}
              <Input
                placeholder="Search Bullhorn categories…"
                value={credentialSearch}
                onChange={(e) => setCredentialSearch(e.target.value)}
                data-testid="credreq-search"
              />
              <div className="border rounded-md max-h-80 overflow-y-auto bg-slate-50">
                {credentialCategoriesLoading ? (
                  <p className="p-3 text-xs text-muted-foreground flex items-center gap-2">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    Loading from Bullhorn…
                  </p>
                ) : credentialCategories.length === 0 ? (
                  <p className="p-3 text-xs text-muted-foreground">
                    No credential categories available. Check Bullhorn connection.
                  </p>
                ) : (
                  credentialCategories
                    .filter(c => !credentialSearch || c.name.toLowerCase().includes(credentialSearch.toLowerCase()))
                    .slice(0, 2000)
                    .map(c => {
                      const checked = selectedCredentialIds.includes(c.id);
                      return (
                        <label
                          key={c.id}
                          className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer border-b last:border-b-0 hover:bg-white min-w-0 ${checked ? 'bg-emerald-50' : ''}`}
                          data-testid={`credreq-cat-${c.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedCredentialIds([...selectedCredentialIds, c.id]);
                              } else {
                                setSelectedCredentialIds(selectedCredentialIds.filter(id => id !== c.id));
                              }
                            }}
                            className="flex-shrink-0"
                          />
                          <span className="truncate flex-1 min-w-0">{c.name.replace(/^\s+/, '')}</span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">#{c.id}</span>
                        </label>
                      );
                    })
                )}
              </div>
            </div>

            {/* Custom message */}
            <div className="space-y-1.5 min-w-0">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Custom message (optional)</Label>
              <Textarea
                value={credentialCustomMessage}
                onChange={(e) => setCredentialCustomMessage(e.target.value)}
                placeholder="Hi! Please upload these so we can get your start date confirmed…"
                rows={3}
                className="w-full max-w-full resize-y"
                data-testid="credreq-custom-message"
              />
            </div>

            <div className="flex justify-end gap-2 pt-3 mt-2 border-t sticky bottom-0 bg-background -mx-6 px-6 -mb-6 pb-4">
              <Button variant="outline" onClick={() => setShowCredentialDialog(false)} disabled={credentialSending} data-testid="credreq-cancel-btn">
                Cancel
              </Button>
              <Button
                onClick={handleSendCredentialRequest}
                disabled={credentialSending || selectedCredentialIds.length === 0 || !recipientEmail}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="credreq-send-btn"
              >
                {credentialSending ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Send Request ({selectedCredentialIds.length})</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Credas journey picker — shown before every send so a recruiter can
          override the tenant's default journey (Settings → Credas) for this
          one candidate, without having to change that default first. */}
      <Dialog open={showCredasDialog} onOpenChange={setShowCredasDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              Send to Credas
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Choose which Credas journey to run for this candidate.
            </p>
            {credasHasActiveCheck && (
              <p className="text-xs text-red-600 mt-1" data-testid="credas-active-check-warning">
                Note: This candidate already has an active Credas check underway.
              </p>
            )}
          </DialogHeader>

          <div className="space-y-2 pt-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Journey</Label>
            {credasJourneysLoading ? (
              <div className="text-xs text-muted-foreground flex items-center py-2">
                <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
                Loading journeys…
              </div>
            ) : credasJourneys.length > 0 ? (
              <Select value={selectedCredasJourneyId} onValueChange={handleCredasJourneyChange}>
                <SelectTrigger data-testid="credas-journey-select">
                  <SelectValue placeholder="Select a journey…" />
                </SelectTrigger>
                <SelectContent>
                  {credasJourneys.map((j) => (
                    <SelectItem key={j.id} value={j.id}>
                      {j.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-red-600">
                No journeys available — check the Credas connection in Settings → Credas.
              </p>
            )}
          </div>

          {/* Actor — always shown, even with only one choice, so the
              recruiter can see exactly which role this candidate will be
              assigned before sending (actors are scoped per-journey; see
              credas.ts's resolveActorId). */}
          <div className="space-y-2 pt-3">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Actor</Label>
            {credasActorsLoading ? (
              <div className="text-xs text-muted-foreground flex items-center py-2">
                <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
                Loading actors…
              </div>
            ) : credasActors.length > 0 ? (
              <Select value={String(selectedCredasActorId)} onValueChange={(v) => setSelectedCredasActorId(Number(v))}>
                <SelectTrigger data-testid="credas-actor-select">
                  <SelectValue placeholder="Select an actor…" />
                </SelectTrigger>
                <SelectContent>
                  {credasActors.map((a) => (
                    <SelectItem key={a.id} value={String(a.id)}>
                      {a.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="text-xs text-red-600">
                No actors found for this journey.
              </p>
            )}
          </div>

          {/* How to send. A channel is only available when the candidate has
              the matching Bullhorn field (email / Mobile Phone); otherwise
              it's greyed out and unchecked. At least one must be ticked. */}
          <div className="space-y-2 pt-3">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Send</Label>
            <div className="flex items-start gap-2">
              <Checkbox
                id="credas-via-email"
                checked={credasViaEmail && Boolean(credasEmail)}
                disabled={credasContact.loading || !credasEmail}
                onCheckedChange={(v) => setCredasViaEmail(v === true)}
                data-testid="credas-send-email-checkbox"
              />
              <label htmlFor="credas-via-email" className={`text-sm leading-tight ${credasEmail ? '' : 'text-muted-foreground'}`}>
                Send via Email
                <span className="block text-xs text-muted-foreground">{credasContact.loading ? 'Loading…' : (credasEmail || 'No email address in Bullhorn')}</span>
              </label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="credas-via-sms"
                checked={credasViaSms && Boolean(credasMobile)}
                disabled={credasContact.loading || !credasMobile}
                onCheckedChange={(v) => setCredasViaSms(v === true)}
                data-testid="credas-send-sms-checkbox"
              />
              <label htmlFor="credas-via-sms" className={`text-sm leading-tight ${credasMobile ? '' : 'text-muted-foreground'}`}>
                Send via SMS
                <span className="block text-xs text-muted-foreground">{credasContact.loading ? 'Loading…' : (credasMobile || 'No phone number in Bullhorn')}</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-3 mt-2 border-t">
            <Button variant="outline" onClick={() => setShowCredasDialog(false)} disabled={credasSending} data-testid="credas-dialog-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleSendCredas}
              disabled={credasSending || credasContact.loading || credasJourneysLoading || credasActorsLoading || !selectedCredasJourneyId || !selectedCredasActorId || !((credasViaEmail && credasEmail) || (credasViaSms && credasMobile))}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="credas-dialog-submit-btn"
            >
              {credasSending ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Submit</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
